import { useState, useEffect, createContext, useContext, useRef, lazy, Suspense, memo, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, useParams, Link } from "react-router-dom";
import axios from "axios";
import { translations, LANGUAGES, COUNTRIES } from "./translations";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { format, addDays } from "date-fns";
// Import contexts from centralized location
import { LanguageProvider, useLanguage, AuthProvider, useAuth, GeoProvider, useGeo } from "./contexts";
import { localizePrices } from "./components/Price";
import { useSEO, useLiveStats } from "./hooks";
import TelegramFloatingButton from "./components/TelegramFloatingButton";

const PRODUCTION_DOMAIN = 'https://mydummyticket.com';

// Manages canonical URL for all pages - prevents preview/staging URLs from getting indexed
const CanonicalManager = () => {
  const location = useLocation();
  useEffect(() => {
    const canonicalPath = location.pathname === '/' ? '' : location.pathname;
    const canonicalUrl = `${PRODUCTION_DOMAIN}${canonicalPath}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
  }, [location.pathname]);
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lazy load pages for code splitting - reduces initial bundle size significantly
const LazyAuthPage = lazy(() => import('./pages/AuthPage'));
const LazyOrdersPage = lazy(() => import('./pages/OrdersPage'));
const LazyFeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const LazyContactPage = lazy(() => import('./pages/ContactPage'));
const LazyBlogListPage = lazy(() => import('./pages/BlogListPage'));
const LazyBlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const LazyPrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const LazyTermsPage = lazy(() => import('./pages/TermsPage'));
const LazyFAQPage = lazy(() => import('./pages/FAQStandalonePage'));
const LazyReferralPage = lazy(() => import('./pages/ReferralPage'));
const LazyFreeResourcesPage = lazy(() => import('./pages/FreeResourcesPage'));
const LazyVisaGuidesHub = lazy(() => import('./pages/VisaGuidesHub'));

// Heavy pages that benefit most from code splitting
const LazyBookingPage = lazy(() => import('./pages/BookingPage'));
const LazyAdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const LazyAdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));

// Country-specific landing pages
const LazyIndiaPage = lazy(() => import('./pages/CountryLandingPages').then(m => ({ default: m.IndiaLandingPage })));
const LazyNigeriaPage = lazy(() => import('./pages/CountryLandingPages').then(m => ({ default: m.NigeriaLandingPage })));
const LazyPakistanPage = lazy(() => import('./pages/CountryLandingPages').then(m => ({ default: m.PakistanLandingPage })));
const LazyThailandPage = lazy(() => import('./pages/CountryLandingPages').then(m => ({ default: m.ThailandLandingPage })));
const LazySchengenVisaPage = lazy(() => import('./pages/VisaLandingPages').then(m => ({ default: m.SchengenVisaPage })));
const LazyUKVisaPage = lazy(() => import('./pages/VisaLandingPages').then(m => ({ default: m.UKVisaPage })));
const LazyThailandOnwardPage = lazy(() => import('./pages/VisaLandingPages').then(m => ({ default: m.ThailandOnwardPage })));
const LazyCanadaVisaPage = lazy(() => import('./pages/VisaLandingPages').then(m => ({ default: m.CanadaVisaPage })));
const LazyUAEVisaPage = lazy(() => import('./pages/VisaLandingPages').then(m => ({ default: m.UAEVisaPage })));
const LazyUSVisaPage = lazy(() => import('./pages/VisaLandingPages').then(m => ({ default: m.USVisaPage })));
const LazyIndiaVisaPage = lazy(() => import('./pages/VisaLandingPages').then(m => ({ default: m.IndiaVisaPage })));

// Schengen country service pages (high-intent SEO landing pages)
const LazyFranceVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.FranceVisaPage })));
const LazyGermanyVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.GermanyVisaPage })));
const LazyItalyVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.ItalyVisaPage })));
const LazySpainVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.SpainVisaPage })));
const LazyNetherlandsVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.NetherlandsVisaPage })));
const LazyBelgiumVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.BelgiumVisaPage })));
const LazySwitzerlandVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.SwitzerlandVisaPage })));
const LazyAustriaVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.AustriaVisaPage })));
const LazyGreeceVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.GreeceVisaPage })));
const LazyPortugalVisaPage = lazy(() => import('./pages/SchengenCountryPages').then(m => ({ default: m.PortugalVisaPage })));

// Loading component for lazy-loaded pages
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTop: '3px solid #0f766e', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
      <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
    </div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Loading Spinner for lazy components
const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <div style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTop: '3px solid #0f766e', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Reusable Trust Badges Component
const TrustBadgesInline = () => (
  <div className="form-card" style={{ 
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
    color: 'white',
    marginBottom: '48px'
  }}>
    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Why Trust Us?</h3>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <img src="https://customer-assets.emergentagent.com/job_flightproof-2/artifacts/aduv5nxr_Information_Commissioners_Office_logo.388db5f2.webp" alt="ICO" style={{ height: '30px' }} />
        <span style={{ opacity: 0.9 }}>ICO Registered: ZC006858</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <img src="https://customer-assets.emergentagent.com/job_flightproof-2/artifacts/wgz88jdu_made-in-britain-logo-white.519a2cb0.webp" alt="Made in Britain" style={{ height: '35px' }} />
        <span style={{ opacity: 0.9 }}>Proudly British Company</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '24px' }}>🔒</span>
        <span style={{ opacity: 0.9 }}>256-bit SSL Encryption</span>
      </div>
    </div>
  </div>
);


// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !startedRef.current) {
          startedRef.current = true;
          let start = 0;
          const endValue = parseFloat(end) || 0;
          const increment = endValue / (duration / 16);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= endValue) {
              setCount(endValue);
              clearInterval(timer);
            } else {
              setCount(start);
            }
          }, 16);
          
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={countRef}>
      {prefix}{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}
    </span>
  );
};

// Searchable Country Select Component
const SearchableCountrySelect = ({ value, onChange, placeholder = "Search country...", testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  const filteredCountries = COUNTRIES.filter(c => 
    (c.name && c.name.toLowerCase().includes(search.toLowerCase())) ||
    (c.code && c.code.toLowerCase().includes(search.toLowerCase()))
  );
  
  const selectedCountry = COUNTRIES.find(c => c.code === value);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        data-testid={testId}
        style={{
          width: '100%',
          height: '42px',
          padding: '0 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          fontSize: '13px',
          background: 'white',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          color: value ? '#0f172a' : '#94a3b8',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedCountry ? `${selectedCountry.flag} ${selectedCountry.name}` : placeholder}</span>
        <span style={{ color: '#6b7a85', flexShrink: 0 }}>▼</span>
      </button>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 100,
          marginTop: '4px',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          maxHeight: '300px',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
            <input
              type="text"
              placeholder="🔍 Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {filteredCountries.map(c => (
              <div
                key={c.code}
                onClick={() => {
                  onChange(c.code, c);
                  setIsOpen(false);
                  setSearch('');
                }}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: value === c.code ? '#f0fdfa' : 'transparent',
                  borderLeft: value === c.code ? '3px solid #0f766e' : '3px solid transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = value === c.code ? '#f0fdfa' : 'transparent'}
              >
                <span style={{ fontSize: '18px' }}>{c.flag}</span>
                <span style={{ flex: 1, fontWeight: value === c.code ? '600' : '400' }}>{c.name}</span>
                <span style={{ fontSize: '12px', color: '#6b7a85' }}>{c.code}</span>
              </div>
            ))}
            {filteredCountries.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7a85' }}>No countries found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Searchable Phone Code Select Component
const SearchablePhoneCodeSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  const filteredCountries = COUNTRIES.filter(c => 
    (c.name && c.name.toLowerCase().includes(search.toLowerCase())) ||
    (c.phone && c.phone.includes(search)) ||
    (c.code && c.code.toLowerCase().includes(search.toLowerCase()))
  );
  
  const selectedCountry = COUNTRIES.find(c => c.phone === value);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div ref={containerRef} style={{ position: 'relative', width: '130px' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '12px 10px',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          fontSize: '14px',
          background: 'white',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <span>{selectedCountry?.flag || '🌍'}</span>
        <span style={{ fontWeight: '500' }}>{value || '+1'}</span>
        <span style={{ color: '#6b7a85', marginLeft: 'auto' }}>▼</span>
      </button>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          width: '280px',
          zIndex: 50,
          marginTop: '4px',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          maxHeight: '300px',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
            <input
              type="text"
              placeholder="🔍 Search country or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {filteredCountries.map(c => (
              <div
                key={c.code}
                onClick={() => {
                  onChange(c.phone);
                  setIsOpen(false);
                  setSearch('');
                }}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: value === c.phone ? '#f0fdfa' : 'transparent',
                  borderLeft: value === c.phone ? '3px solid #0f766e' : '3px solid transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = value === c.phone ? '#f0fdfa' : 'transparent'}
              >
                <span style={{ fontSize: '18px' }}>{c.flag}</span>
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ fontWeight: '600', color: '#0f766e' }}>{c.phone}</span>
              </div>
            ))}
            {filteredCountries.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7a85' }}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// Country Code Selector Component for Phone Numbers
const CountryCodeSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  // Use COUNTRIES from translations which has all countries with phone codes
  const countryCodes = COUNTRIES.filter(c => c.phone).map(c => ({
    code: c.phone,
    country: c.code,
    name: c.name,
    flag: c.flag
  }));
  
  const selected = countryCodes.find(c => c.code === value) || countryCodes.find(c => c.code === '+44') || countryCodes[0];
  
  const filteredCodes = search 
    ? countryCodes.filter(c => 
        c.code.includes(search) || 
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.country.toLowerCase().includes(search.toLowerCase())
      )
    : countryCodes;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: '100px' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 8px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          background: 'white',
          fontSize: '13px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          justifyContent: 'space-between',
          height: '42px',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {selected.flag} {selected.code}
        </span>
        <span style={{ fontSize: '9px', color: '#6b7a85' }}>▼</span>
      </button>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          width: '220px',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          maxHeight: '280px',
          overflow: 'hidden',
          zIndex: 1000,
          marginTop: '4px'
        }}>
          <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {filteredCodes.slice(0, 50).map(country => (
              <div
                key={country.code + country.country}
                onClick={() => { onChange(country.code); setIsOpen(false); setSearch(''); }}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  background: country.code === value ? '#f0fdfa' : 'white'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = country.code === value ? '#f0fdfa' : 'white'}
              >
                <span>{country.flag}</span>
                <span style={{ fontWeight: '500' }}>{country.code}</span>
                <span style={{ color: '#5f717e', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Header Component
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, language, changeLanguage, LANGUAGES } = useLanguage();
  const { currency, currencies, setCurrency } = useGeo();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showCurMenu, setShowCurMenu] = useState(false);

  const scrollTo = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentLang = LANGUAGES.find(l => l.code === language);

  return (
    <header className="header">
      <div className="header-content">
        <div className="logo" onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="logo" style={{ cursor: 'pointer' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_ticket-system-89/artifacts/85tijzvo_MYDUMMYTICKET-AI.svg" 
            alt="My Dummy Ticket - Verifiable Flight Reservations for Visa Applications" 
            style={{ height: '58px' }}
            width="200"
            height="58"
            fetchPriority="high"
          />
        </div>

        {/* Navigation links moved to footer - keeping header minimal like competitors */}
        <nav className="nav-links" style={{ display: 'none' }}>
        </nav>

        <div className="header-actions">
          {/* Currency Selector (display-only; Stripe always charges GBP) */}
          <div style={{ position: 'relative' }}>
            <button 
              className="track-btn" 
              onClick={() => setShowCurMenu(!showCurMenu)}
              style={{ padding: '10px 14px', minWidth: 'auto', fontSize: '15px' }}
              data-testid="currency-selector"
            >
              {currency?.symbol} {currency?.code}
            </button>
            {showCurMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                padding: '8px 0',
                minWidth: '150px',
                zIndex: 100,
                marginTop: '8px',
                maxHeight: '320px',
                overflowY: 'auto'
              }}>
                {currencies.map(c => (
                  <button 
                    key={c.code}
                    onClick={() => { setCurrency(c.code); setShowCurMenu(false); }}
                    style={{ 
                      width: '100%', 
                      padding: '10px 16px', 
                      background: currency?.code === c.code ? '#f0f9ff' : 'none', 
                      border: 'none', 
                      textAlign: 'left', 
                      cursor: 'pointer', 
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontWeight: currency?.code === c.code ? '700' : '400'
                    }}
                    data-testid={`currency-${c.code}`}
                  >
                    <span style={{ minWidth: '28px' }}>{c.symbol}</span>
                    <span>{c.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Language Selector */}
          <div style={{ position: 'relative' }}>
            <button 
              className="track-btn" 
              onClick={() => setShowLangMenu(!showLangMenu)}
              style={{ padding: '10px 14px', minWidth: 'auto', fontSize: '15px' }}
              data-testid="lang-selector"
            >
              {currentLang?.flag} {currentLang?.code.toUpperCase()}
            </button>
            {showLangMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                padding: '8px 0',
                minWidth: '160px',
                zIndex: 100,
                marginTop: '8px'
              }}>
                {LANGUAGES.map(lang => (
                  <button 
                    key={lang.code}
                    onClick={() => { changeLanguage(lang.code); setShowLangMenu(false); }}
                    style={{ 
                      width: '100%', 
                      padding: '10px 16px', 
                      background: language === lang.code ? '#f0f9ff' : 'none', 
                      border: 'none', 
                      textAlign: 'left', 
                      cursor: 'pointer', 
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span style={{ fontSize: '18px' }}>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div style={{ position: 'relative' }}>
              <button 
                className="track-btn" 
                onClick={() => setShowUserMenu(!showUserMenu)}
                data-testid="user-menu-btn"
              >
                👤 {user.first_name}
              </button>
              {showUserMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  padding: '8px 0',
                  minWidth: '180px',
                  zIndex: 100
                }}>
                  <button 
                    onClick={() => { navigate('/orders'); setShowUserMenu(false); }}
                    style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px' }}
                    data-testid="my-orders-btn"
                  >
                    📋 {t.nav.myOrders}
                  </button>
                  <button 
                    onClick={() => { logout(); setShowUserMenu(false); }}
                    style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#dc2626' }}
                    data-testid="logout-btn"
                  >
                    🚪 {t.nav.logout}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="track-btn" onClick={() => navigate('/login')} data-testid="login-btn">
              👤 {t.nav.login}
            </button>
          )}
          <button className="book-btn" onClick={() => { navigate('/order'); window.scrollTo(0, 0); }} data-testid="book-now-btn">
            {t.nav.bookNow}
          </button>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ onBook }) => {
  const { t } = useLanguage();
  const { format } = useGeo();
  const liveStats = useLiveStats();
  
  return (
    <section className="hero-section">
      <div className="hero-decoration decoration-1"></div>
      <div className="hero-decoration decoration-2"></div>

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '800px' }}>
        <div className="live-badge">
          <span className="dot-live"></span>
          Genuine PNR · Verifiable on CheckMyTrip · UK ICO Registered · Made in Britain
        </div>

        <h1 className="hero-title">
          Get Your <span className="highlight-teal">Dummy Ticket</span> for Visa Applications in 1Min
        </h1>

        <p className="hero-subtitle" style={{ fontSize: '18px', lineHeight: '1.7', maxWidth: '650px', margin: '0 auto 24px' }}>
          Need a <strong>dummy ticket for visa</strong> application? MyDummyTicket provides verified <strong>flight reservations for visa</strong> with real PNR codes, 
          trusted by embassies for Schengen, UK, and USA visas. Whether you need a <strong>proof of onward travel</strong>, dummy booking, or dummy air ticket — 
          get instant delivery to your email from just {format(6.99)}.
        </p>

        <div className="cta-group">
          <button className="btn-primary" onClick={onBook} data-testid="hero-book-btn" style={{ fontSize: '17px', padding: '18px 36px' }}>
            🎫 Get Your Flight Reservation Now
          </button>
          <Link to="/templates" className="btn-secondary" data-testid="view-sample-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
            View Sample Tickets
          </Link>
        </div>

        <div className="trust-strip">
          <div className="trust-pill">🔒 Stripe Secured</div>
          <div className="trust-pill">✅ {t.hero.trustBadge3}</div>
          <div className="trust-pill">⚡ {t.hero.trustBadge1}</div>
          <div className="trust-pill">🇬🇧 UK Based</div>
          <div className="trust-pill">💬 24/7 Support</div>
        </div>

        <div className="stats-row">
          <div className="stat-item">
            <div className="stat-num">+50K</div>
            <div className="stat-label">Happy Travellers</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">+150</div>
            <div className="stat-label">Countries Served</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">99.8%</div>
            <div className="stat-label">Delivery Rate</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">~1 min</div>
            <div className="stat-label">Avg Delivery</div>
          </div>
        </div>
        
        {/* Trustpilot Badge - Compact */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '16px',
          marginTop: '32px',
          padding: '16px 24px',
          background: 'rgba(255,255,255,0.9)',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          {/* Trustpilot Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6L12 2z" fill="#00B67A"/>
            </svg>
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#191919', letterSpacing: '-0.5px' }}>Trustpilot</span>
          </div>
          
          {/* Stars */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ 
                width: '24px', height: '24px', 
                background: i <= 4 ? '#00B67A' : 'linear-gradient(90deg, #00B67A 70%, #dcdce6 70%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
            ))}
          </div>
          
          {/* Rating */}
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: '700', fontSize: '15px', color: '#191919' }}>4.7 out of 5</div>
            <div style={{ fontSize: '12px', color: '#5f717e' }}>Based on 754 reviews</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Flight + Hotel Bundle Promo Section
const FlightHotelPromo = ({ onBook }) => {
  const { format } = useGeo();
  return (
    <section style={{ 
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fcd34d 100%)', 
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative elements */}
      <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '150px', height: '150px', background: 'rgba(255,255,255,0.3)', borderRadius: '50%' }}></div>
      <div style={{ position: 'absolute', bottom: '-30px', left: '10%', width: '100px', height: '100px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%' }}></div>
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '40px',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {/* Left: Icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ 
              background: 'white', 
              borderRadius: '16px', 
              padding: '16px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <span style={{ fontSize: '36px' }}>✈️</span>
            </div>
            <span style={{ fontSize: '28px', color: '#d97706' }}>+</span>
            <div style={{ 
              background: 'white', 
              borderRadius: '16px', 
              padding: '16px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <span style={{ fontSize: '36px' }}>🏨</span>
            </div>
          </div>
          
          {/* Center: Text */}
          <div style={{ textAlign: 'center', flex: '1', minWidth: '280px' }}>
            <div style={{ 
              display: 'inline-block',
              background: '#dc2626', 
              color: 'white', 
              padding: '6px 16px', 
              borderRadius: '20px', 
              fontSize: '13px', 
              fontWeight: '700',
              marginBottom: '12px',
              animation: 'pulse 2s infinite'
            }}>
              ✈️ VISA-READY FLIGHT RESERVATIONS
            </div>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: '800', 
              color: '#0f172a', 
              marginBottom: '8px',
              lineHeight: '1.3'
            }}>
              Flight + Hotel Bundle
            </h2>
            <p style={{ fontSize: '14px', color: '#78350f', maxWidth: '400px', margin: '0 auto' }}>
              Get both your flight reservation AND hotel booking confirmation for visa applications. Complete travel documentation in one order.
            </p>
          </div>
          
          {/* Right: CTA */}
          <div style={{ textAlign: 'center' }}>
            <button 
              onClick={onBook}
              style={{
                background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                color: 'white',
                border: 'none',
                padding: '16px 32px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(15, 118, 110, 0.4)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(15, 118, 110, 0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(15, 118, 110, 0.4)'; }}
              data-testid="bundle-cta"
            >
              Book Bundle & Save →
            </button>
            <p style={{ fontSize: '12px', color: '#92400e', marginTop: '8px' }}>
              Starting from <strong>{format(16.99)}</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Airline Logo Scroller - Shows trusted airline partners
const AirlineLogoScroller = () => {
  const airlines = [
    { name: 'Emirates', logo: 'https://upload.wikimedia.org/wikipedia/commons/d/d0/Emirates_logo.svg' },
    { name: 'British Airways', logo: 'https://upload.wikimedia.org/wikipedia/en/4/42/British_Airways_Logo.svg' },
    { name: 'Lufthansa', logo: 'https://upload.wikimedia.org/wikipedia/commons/b/b8/Lufthansa_Logo_2018.svg' },
    { name: 'Air France', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Air_France_Logo.svg' },
    { name: 'KLM', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c7/KLM_logo.svg' },
    { name: 'Qatar Airways', logo: 'https://upload.wikimedia.org/wikipedia/en/9/9b/Qatar_Airways_Logo.svg' },
    { name: 'Turkish Airlines', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/00/Turkish_Airlines_logo_2019_compact.svg' },
    { name: 'Singapore Airlines', logo: 'https://upload.wikimedia.org/wikipedia/en/6/6b/Singapore_Airlines_Logo_2.svg' },
    { name: 'Swiss', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/f8/Swiss_International_Air_Lines_Logo_2011.svg' },
    { name: 'Etihad', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Etihad-airways-logo.svg' },
  ];
  
  return (
    <section style={{ background: '#f8fafc', padding: '40px 0', overflow: 'hidden' }}>
      <div className="container" style={{ textAlign: 'center', marginBottom: '24px' }}>
        <p style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>
          We book reservations with trusted airlines
        </p>
      </div>
      <div style={{ 
        display: 'flex',
        animation: 'scroll 30s linear infinite',
        width: 'fit-content'
      }}>
        {[...airlines, ...airlines, ...airlines].map((airline, i) => (
          <div 
            key={i} 
            style={{ 
              minWidth: '160px', 
              height: '60px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '0 24px',
              opacity: 0.6,
              filter: 'grayscale(100%)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.filter = 'grayscale(0%)'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.filter = 'grayscale(100%)'; }}
          >
            <img 
              src={airline.logo} 
              alt={airline.name}
              style={{ maxHeight: '40px', maxWidth: '120px', objectFit: 'contain' }}
              loading="lazy"
              width="120"
              height="40"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        ))}
      </div>
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  return (
    <section className="section" style={{ background: 'linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%)', padding: '80px 0' }}>
      <div className="container">
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <div className="eyebrow" style={{ marginBottom: '12px' }}>Trusted Worldwide</div>
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '20px', lineHeight: '1.3' }}>
            Why Choose MyDummyTicket?
          </h2>
          <p style={{ fontSize: '17px', color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '40px' }}>
            We provide verifiable flight reservations with <strong>real airline PNR codes</strong>. 
            Unlike fake itineraries, our dummy flight tickets can be verified on <strong>CheckMyTrip</strong> and airline websites. 
            Trusted by thousands for proof of onward travel, visa applications, and border crossing requirements.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
            <div style={{ background: 'white', padding: '28px 20px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(15,118,110,0.08)', border: '1px solid rgba(15,118,110,0.1)' }}>
              <div style={{ width: '56px', height: '56px', background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px', color: 'white' }}>✓</div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>Real PNR Codes</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>Verifiable on airline websites & CheckMyTrip</p>
            </div>
            <div style={{ background: 'white', padding: '28px 20px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(15,118,110,0.08)', border: '1px solid rgba(15,118,110,0.1)' }}>
              <div style={{ width: '56px', height: '56px', background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px', color: 'white' }}>⚡</div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>Instant Delivery</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>Email delivery within 1 minute</p>
            </div>
            <div style={{ background: 'white', padding: '28px 20px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(15,118,110,0.08)', border: '1px solid rgba(15,118,110,0.1)' }}>
              <div style={{ width: '56px', height: '56px', background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px', color: 'white' }}>📋</div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>Visa Ready</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>Accepted by embassies worldwide</p>
            </div>
            <div style={{ background: 'white', padding: '28px 20px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(15,118,110,0.08)', border: '1px solid rgba(15,118,110,0.1)' }}>
              <div style={{ width: '56px', height: '56px', background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px', color: 'white' }}>💬</div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>24/7 Support</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>Customer support always available</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const { t } = useLanguage();
  
  return (
    <section className="section" id="how">
      <div className="container">
        <div className="section-header">
          <div className="eyebrow">Process</div>
          <h2 className="section-title">{t.howItWorks.title}</h2>
          <p className="section-desc">{t.howItWorks.subtitle}</p>
        </div>

        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">01</div>
            <div className="step-icon">📝</div>
            <h3 className="step-title">{t.howItWorks.step1Title}</h3>
            <p className="step-desc">{t.howItWorks.step1Desc}</p>
          </div>
          <div className="step-card">
            <div className="step-number">02</div>
            <div className="step-icon">💳</div>
            <h3 className="step-title">{t.howItWorks.step2Title}</h3>
            <p className="step-desc">{t.howItWorks.step2Desc}</p>
          </div>
          <div className="step-card">
            <div className="step-number">03</div>
            <div className="step-icon">✈️</div>
            <h3 className="step-title">{t.howItWorks.step3Title}</h3>
            <p className="step-desc">{t.howItWorks.step3Desc}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onSelectPlan, pricing }) => {
  const { t } = useLanguage();
  const geo = useGeo();
  const sym = geo?.currency?.symbol || '£';
  const rate = geo?.currency?.rate || 1;
  const conv = (v) => (parseFloat(v || 0) * rate).toFixed(rate >= 100 ? 0 : 2);
  const [tripType, setTripType] = useState('one_way');
  const [validity, setValidity] = useState('24_hours');

  const validityOptions = [
    { key: '24_hours', label: '24 Hours', sublabel: 'Quick Verification' },
    { key: '48_hours', label: '48 Hours', sublabel: 'Recommended', popular: true }
  ];

  // Matrix pricing — USD display (Stripe charges GBP). Standard is flat;
  // verifiable/bundle vary by validity (24h/48h) and trip type (one_way/round_trip).
  const matrix = pricing?.pricing_matrix || {
    standard: { gbp: { one_way: 6.99, round_trip: 6.99 } },
    verifiable: {
      '24_hours': { gbp: { one_way: 11.99, round_trip: 14.99 } },
      '48_hours': { gbp: { one_way: 14.99, round_trip: 17.99 } },
    },
    bundle: {
      '24_hours': { gbp: { one_way: 16.99, round_trip: 19.99 } },
      '48_hours': { gbp: { one_way: 19.99, round_trip: 22.99 } },
    },
  };
  const gbpPrice = (tier) => {
    const tp = matrix[tier] || matrix.standard;
    const block = tier === 'standard' ? tp.gbp : (tp[validity] || tp['48_hours']).gbp;
    return block[tripType] ?? block.round_trip;
  };
  const currentStandardPrice = gbpPrice('standard');
  const currentVerifiablePrice = gbpPrice('verifiable');
  const bundlePrice = gbpPrice('bundle');

  return (
    <section className="section section-alt" id="pricing">
      <div className="container">
        <div className="section-header">
          <div className="eyebrow">Pricing</div>
          <h2 className="section-title">{t.pricingSection.title}</h2>
          <p className="section-desc">{t.pricingSection.subtitle}</p>
        </div>

        {/* Trip Type Selector */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
          <div style={{ 
            display: 'inline-flex', 
            background: '#f1f5f9', 
            borderRadius: '12px', 
            padding: '4px',
            gap: '4px'
          }}>
            <button
              onClick={() => setTripType('one_way')}
              data-testid="trip-type-one-way"
              style={{
                padding: '12px 28px',
                borderRadius: '10px',
                border: 'none',
                background: tripType === 'one_way' ? 'white' : 'transparent',
                boxShadow: tripType === 'one_way' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                fontWeight: tripType === 'one_way' ? '600' : '500',
                color: tripType === 'one_way' ? '#0f172a' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px'
              }}
            >
              ✈️ One-way
            </button>
            <button
              onClick={() => setTripType('round_trip')}
              data-testid="trip-type-round-trip"
              style={{
                padding: '12px 28px',
                borderRadius: '10px',
                border: 'none',
                background: tripType === 'round_trip' ? 'white' : 'transparent',
                boxShadow: tripType === 'round_trip' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                fontWeight: tripType === 'round_trip' ? '600' : '500',
                color: tripType === 'round_trip' ? '#0f172a' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px'
              }}
            >
              🔄 Round-trip
            </button>
          </div>
        </div>

        {/* Three Column Pricing Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '20px', 
          maxWidth: '1100px', 
          margin: '0 auto' 
        }}>
          
          {/* Standard Tier */}
          <div 
            className="plan-card" 
            data-testid="plan-standard"
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '28px',
              border: '2px solid #e2e8f0',
              position: 'relative'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '24px', marginTop: '8px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Standard</h3>
              <p style={{ fontSize: '13px', color: '#5f717e' }}>PDF Itinerary • 48h Validity</p>
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px' }}>
                <span style={{ fontSize: '24px', fontWeight: '600', color: '#5f717e' }}>{sym}</span>
                <span style={{ fontSize: '48px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>{conv(currentStandardPrice)}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#5f717e', marginTop: '6px' }}>per adult • delivered instantly</p>
              {false && tripType === 'round_trip' && (
                <span style={{ 
                  display: 'inline-block',
                  background: '#dcfce7', 
                  color: '#16a34a', 
                  padding: '4px 12px', 
                  borderRadius: '20px', 
                  fontSize: '12px', 
                  fontWeight: '600',
                  marginTop: '8px'
                }}>
                  Save 25% Bundle
                </span>
              )}
            </div>
            
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151' }}>Professional PDF document</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151' }}>Instant email delivery</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151' }}>Email delivery</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151' }}>Live order tracking</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#ef4444', fontSize: '16px' }}>✗</span>
                <span style={{ fontSize: '14px', color: '#6b7a85' }}>No PNR verification</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
                <span style={{ color: '#ef4444', fontSize: '16px' }}>✗</span>
                <span style={{ fontSize: '14px', color: '#6b7a85' }}>Not verifiable online</span>
              </li>
            </ul>
            
            <button 
              className="plan-btn outline" 
              onClick={() => onSelectPlan('standard', '48_hours', tripType)} 
              data-testid="select-standard"
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '12px',
                border: '2px solid #e2e8f0',
                background: 'white',
                color: '#374151',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Select Standard
            </button>
            
            <p style={{ fontSize: '11px', color: '#6b7a85', textAlign: 'center', marginTop: '16px', lineHeight: '1.5' }}>
              Best for basic travel planning & low-risk applications
            </p>
          </div>

          {/* Verifiable Tier - Middle Column (Highlighted) */}
          <div 
            className="plan-card recommended" 
            data-testid="plan-verifiable"
            style={{
              background: 'linear-gradient(135deg, #f0fdfa 0%, #ffffff 100%)',
              borderRadius: '20px',
              padding: '28px',
              border: '3px solid #0f766e',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(15, 118, 110, 0.15)',
              transform: 'scale(1.02)'
            }}
          >
            {tripType === 'round_trip' && validity === '48_hours' ? (
              <div style={{ 
                position: 'absolute', 
                top: '-12px', 
                left: '50%', 
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap'
              }}>
                ⭐ MOST POPULAR FOR SCHENGEN VISA
              </div>
            ) : (
              <div style={{ 
                position: 'absolute', 
                top: '-12px', 
                left: '50%', 
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.5px'
              }}>
                ⭐ RECOMMENDED
              </div>
            )}
            
            <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '8px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🛡️</div>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Verifiable PNR</h3>
              <p style={{ fontSize: '13px', color: '#5f717e' }}>Real Airline Reservation • CheckMyTrip Verified</p>
            </div>
            
            {/* Validity Tabs inside Verifiable */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '6px', 
              marginBottom: '20px',
              background: '#f1f5f9',
              padding: '4px',
              borderRadius: '10px'
            }}>
              {validityOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setValidity(opt.key)}
                  data-testid={`validity-${opt.key}`}
                  style={{
                    padding: '8px 4px',
                    borderRadius: '8px',
                    border: 'none',
                    background: validity === opt.key ? '#0f766e' : 'transparent',
                    color: validity === opt.key ? 'white' : '#64748b',
                    fontSize: '12px',
                    fontWeight: validity === opt.key ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                >
                  {opt.label}
                  {opt.popular && validity !== opt.key && (
                    <span style={{ 
                      position: 'absolute', 
                      top: '-6px', 
                      right: '-2px', 
                      background: '#f59e0b', 
                      color: 'white', 
                      fontSize: '8px', 
                      padding: '2px 4px', 
                      borderRadius: '4px',
                      fontWeight: '700'
                    }}>★</span>
                  )}
                </button>
              ))}
            </div>
            
            <div style={{ 
              textAlign: 'center', 
              padding: '8px 12px', 
              background: '#ecfdf5', 
              borderRadius: '8px', 
              marginBottom: '16px',
              fontSize: '12px',
              color: '#047857',
              fontWeight: '500'
            }}>
              {validityOptions.find(v => v.key === validity)?.sublabel}
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px' }}>
                <span style={{ fontSize: '24px', fontWeight: '600', color: '#0f766e' }}>{sym}</span>
                <span style={{ fontSize: '48px', fontWeight: '800', color: '#0f766e', lineHeight: 1 }}>{conv(currentVerifiablePrice)}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#0f766e', marginTop: '6px' }}>per adult • 1 min – 6 hours</p>
            </div>
            
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #ccfbf1' }}>
                <span style={{ color: '#0f766e', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Real PNR code</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #ccfbf1' }}>
                <span style={{ color: '#0f766e', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Verifiable on CheckMyTrip</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #ccfbf1' }}>
                <span style={{ color: '#0f766e', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Verifiable on airline website</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #ccfbf1' }}>
                <span style={{ color: '#0f766e', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Real airline + flight number</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #ccfbf1' }}>
                <span style={{ color: '#0f766e', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Schengen / UK / US / Canada ready</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
                <span style={{ color: '#0f766e', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>All Standard features included</span>
              </li>
            </ul>
            
            <button 
              className="plan-btn filled" 
              onClick={() => onSelectPlan('verifiable', validity, tripType)} 
              data-testid="select-verifiable"
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(15, 118, 110, 0.3)'
              }}
            >
              Select Verifiable →
            </button>
            
            <p style={{ fontSize: '11px', color: '#0f766e', textAlign: 'center', marginTop: '16px', lineHeight: '1.5', fontWeight: '500' }}>
              Recommended for Schengen, UK, US, Canada & Australia visas
            </p>
          </div>

          {/* Flight + Hotel Bundle Tier */}
          <div 
            className="plan-card bundle" 
            data-testid="plan-bundle"
            style={{
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              borderRadius: '20px',
              padding: '28px',
              border: '3px solid #f59e0b',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(245, 158, 11, 0.2)'
            }}
          >
            <div style={{ 
              position: 'absolute', 
              top: '-12px', 
              left: '50%', 
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '700',
              letterSpacing: '0.5px',
              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
            }}>
              🔥 BEST VALUE
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '8px' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>✈️ + 🏨</div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Flight + Hotel</h3>
              <p style={{ fontSize: '12px', color: '#92400e' }}>Complete Visa Package • Flight + Hotel</p>
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px' }}>
                <span style={{ fontSize: '22px', fontWeight: '600', color: '#92400e' }}>{sym}</span>
                <span style={{ fontSize: '42px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>{conv(bundlePrice)}</span>
              </div>
              <span style={{ 
                display: 'inline-block',
                background: '#dc2626', 
                color: 'white', 
                padding: '4px 12px', 
                borderRadius: '20px', 
                fontSize: '11px', 
                fontWeight: '700',
                marginTop: '8px'
              }}>
                per adult • real hotel confirmation
              </span>
            </div>
            
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #fde68a' }}>
                <span style={{ color: '#f59e0b', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Verifiable Flight + PNR</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #fde68a' }}>
                <span style={{ color: '#f59e0b', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Hotel Booking Confirmation</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #fde68a' }}>
                <span style={{ color: '#f59e0b', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>CheckMyTrip verification</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #fde68a' }}>
                <span style={{ color: '#f59e0b', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Complete visa documentation</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0' }}>
                <span style={{ color: '#f59e0b', fontSize: '16px', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>All Verifiable features</span>
              </li>
            </ul>
            
            <button 
              className="plan-btn bundle" 
              onClick={() => onSelectPlan('bundle', '48_hours', tripType)} 
              data-testid="select-bundle"
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
              }}
            >
              Get Bundle Deal →
            </button>
            
            <p style={{ fontSize: '11px', color: '#92400e', textAlign: 'center', marginTop: '14px', lineHeight: '1.5', fontWeight: '500' }}>
              Best for complete visa applications
            </p>
          </div>
        </div>

        {/* Family-friendly pricing note */}
        <div style={{ 
          marginTop: '40px', 
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)', 
          padding: '24px 32px', 
          borderRadius: '16px',
          border: '2px solid #99f6e4',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f766e' }}>
            👨‍👩‍👧‍👦 Family-Friendly Pricing
          </h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap' }}>
            <div style={{ 
              background: 'white', 
              padding: '16px 24px', 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--accent-teal)' }}>25% OFF</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Per Child (2–11 yrs)</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '16px 24px', 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--accent-teal)' }}>~67% OFF</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Per Infant (under 2)</div>
            </div>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '16px' }}>
            Perfect for family visa applications. Reduced child &amp; infant rates applied automatically at checkout.
          </p>
        </div>
      </div>
    </section>
  );
};

// Templates Section
const TemplatesSection = () => {
  const templates = [
    { icon: '📝', title: 'Visa Cover Letter', desc: 'Embassy-approved format.' },
    { icon: '🗺️', title: 'Travel Itinerary', desc: 'Day-by-day travel plan.' },
    { icon: '✈️', title: 'Onward Travel Letter', desc: 'Proof of departure.' },
    { icon: '✉️', title: 'Airline Clarification', desc: 'Reservation vs ticket.' },
    { icon: '💑', title: 'Spouse Sponsorship', desc: 'Schengen couples.' },
    { icon: '👨‍👩‍👧‍👦', title: 'Children Sponsorship', desc: 'Minors visa.' },
    { icon: '🗺️', title: 'Schengen Itinerary', desc: 'Multi-country.' },
    { icon: '💰', title: 'Financial Declaration', desc: 'Assets statement.' }
  ];

  return (
    <section className="section" id="templates">
      <div className="container">
        <div className="section-header">
          <div className="eyebrow">Free Resources</div>
          <h2 className="section-title">Visa Application Templates</h2>
          <p className="section-desc">8 professional templates — completely free to download</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', maxWidth: '1000px', margin: '0 auto' }}>
          {templates.map((tpl, i) => (
            <div key={i} className="step-card" style={{ textAlign: 'center', padding: '24px' }} data-testid={`template-${i}`}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>{tpl.icon}</div>
              <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px' }}>{tpl.title}</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>{tpl.desc}</p>
              <button className="plan-btn outline" style={{ padding: '8px 16px', fontSize: '13px' }}>Download</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = ({ faqData }) => {
  const { t } = useLanguage();
  const { format } = useGeo();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = faqData || [
    { question: 'What exactly is a dummy ticket?', answer: 'A dummy ticket is a real flight reservation with a PNR code verifiable on airline websites.' },
  ];

  return (
    <section className="section section-alt" id="faq">
      <div className="container" style={{ maxWidth: '700px' }}>
        <div className="section-header">
          <div className="eyebrow">FAQ</div>
          <h2 className="section-title">{t.faq.title}</h2>
          <p className="section-desc">{t.faq.subtitle}</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className="step-card"
              style={{ padding: 0, overflow: 'hidden' }}
              data-testid={`faq-${i}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'left'
                }}
              >
                {faq.question}
                <span style={{ color: 'var(--accent-teal)', fontSize: '20px', fontWeight: '300' }}>
                  {openIndex === i ? '−' : '+'}
                </span>
              </button>
              {openIndex === i && (
                <div style={{ padding: '0 24px 20px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.7' }}>
                  {localizePrices(faq.answer, format)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Use Cases Section - Why customers need dummy tickets
const UseCasesSection = () => {
  const useCases = [
    {
      icon: '🎫',
      bgColor: '#f0fdfa',
      title: 'Schengen & Embassy Visa Support',
      description: 'Meet embassy flight itinerary requirements without purchasing expensive tickets upfront.',
      points: [
        'Accepted for Schengen, UK, US, Canada visas',
        'Professional PNR confirmation format',
        'Instant delivery to your inbox'
      ]
    },
    {
      icon: '🛫',
      bgColor: '#ecfdf5',
      title: 'Proof of Onward Travel',
      description: 'Show immigration officers your confirmed return flight reservation at any border checkpoint.',
      points: [
        'Valid for airline check-in requirements',
        'Satisfies onward ticket rules worldwide',
        'Change dates without penalty fees'
      ]
    },
    {
      icon: '🏢',
      bgColor: '#f0fdfa',
      title: 'Corporate & HR Documentation',
      description: 'Get travel approval from your employer with official flight reservation documents.',
      points: [
        'Perfect for work leave applications',
        'Budget planning documentation',
        'No upfront flight purchase needed'
      ]
    },
    {
      icon: '🛡️',
      bgColor: '#ecfdf5',
      title: 'Risk-Free Travel Planning',
      description: 'Plan your trip confidently without committing thousands to flights before visa approval.',
      points: [
        'Zero financial risk if visa denied',
        'No airline cancellation charges',
        'Flexible booking modifications'
      ]
    }
  ];

  return (
    <section className="section" id="use-cases" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, white 100%)' }}>
      <div className="container">
        <div className="section-header">
          <h2 className="section-title" style={{ color: '#1e3a5f', fontSize: '32px', marginBottom: '16px' }}>
            Why Travellers Choose Dummy Flight Tickets
          </h2>
          <p className="section-desc" style={{ maxWidth: '800px', margin: '0 auto' }}>
            Discover how our flight reservation service helps with visa applications, immigration requirements, and smart travel planning — trusted by 50,000+ customers.
          </p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px', maxWidth: '900px', margin: '0 auto' }}>
          {useCases.map((useCase, index) => (
            <div 
              key={index}
              style={{ 
                background: useCase.bgColor, 
                borderRadius: '16px', 
                padding: '28px',
                border: '1px solid #d1fae5'
              }}
            >
              {/* Icon */}
              <div style={{ 
                width: '52px', 
                height: '52px', 
                background: 'white', 
                borderRadius: '12px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '24px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}>
                {useCase.icon}
              </div>
              
              {/* Title */}
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#1e3a5f', 
                marginBottom: '12px' 
              }}>
                {useCase.title}
              </h3>
              
              {/* Description */}
              <p style={{ 
                fontSize: '14px', 
                color: '#5f717e', 
                marginBottom: '16px', 
                lineHeight: '1.6' 
              }}>
                {useCase.description}
              </p>
              
              {/* Points */}
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {useCase.points.map((point, i) => (
                  <li key={i} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px', 
                    padding: '6px 0', 
                    fontSize: '13px', 
                    color: '#374151' 
                  }}>
                    <span style={{ color: '#0f766e', fontWeight: '600' }}>✓</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
      </div>
    </section>
  );
};

// Track Order Section (kept for reference, but removed from landing page)

// SEO Keyword Content Sections - "What is a Dummy Ticket?", "Why You Need One", "How It Works"
const DummyTicketExplainerSection = () => {
  const { format } = useGeo();
  return (
    <section className="section" style={{ background: '#fff' }}>
      <div className="container" style={{ maxWidth: '900px' }}>
        
        {/* Section 1: What is a Dummy Ticket? */}
        <div style={{ marginBottom: '60px' }}>
          <div className="section-header">
            <div className="eyebrow">Understanding Dummy Tickets</div>
            <h2 className="section-title" style={{ fontSize: '28px' }}>What is a Dummy Ticket?</h2>
          </div>
          <div style={{ fontSize: '15px', lineHeight: '1.8', color: '#374151' }}>
            <p style={{ marginBottom: '16px' }}>
              A <strong>dummy ticket</strong> (also known as a <strong>dummy flight ticket</strong>, <strong>dummy air ticket</strong>, or <strong>dummy booking</strong>) is a genuine flight reservation that contains a real PNR (Passenger Name Record) code. Unlike a purchased airline ticket, a dummy ticket is a temporary reservation used specifically for <strong>visa applications</strong>, <strong>proof of onward travel</strong>, and immigration requirements.
            </p>
            <p style={{ marginBottom: '16px' }}>
              When you get a <strong>dummy ticket</strong> from MyDummyTicket (also searched as <strong>dummyticket</strong> or <strong>my dummy ticket</strong>), you receive a professional flight itinerary PDF with a verifiable PNR code that can be checked on airline websites or <strong>CheckMyTrip.com</strong>. This is exactly what embassies, consulates, and immigration officers require when processing your visa application.
            </p>
            <p>
              A <strong>ticket dummy</strong> service like ours saves you from spending hundreds or thousands of pounds on actual flight tickets before your visa is even approved. It's the smart, risk-free way to meet travel documentation requirements — trusted by over 50,000 travellers worldwide and accepted for Schengen, UK, US, Canada, and 150+ country visa applications.
            </p>
          </div>
        </div>

        {/* Section 2: Why You Need a Dummy Ticket for Visas */}
        <div style={{ marginBottom: '60px' }}>
          <div className="section-header">
            <div className="eyebrow">Visa Requirements</div>
            <h2 className="section-title" style={{ fontSize: '28px' }}>Why You Need a Dummy Ticket for Visas</h2>
          </div>
          <div style={{ fontSize: '15px', lineHeight: '1.8', color: '#374151' }}>
            <p style={{ marginBottom: '16px' }}>
              Most embassies and consulates require a <strong>confirmed flight reservation</strong> as part of your visa application. However, they explicitly advise applicants <strong>not to purchase actual tickets</strong> until the visa is approved — because if your visa is denied, you lose the money spent on non-refundable flights. This is where a <strong>dummy flight booking</strong> becomes essential.
            </p>
            <p style={{ marginBottom: '16px' }}>
              A <strong>dummy ticket for visa</strong> applications solves this catch-22 perfectly. With a verified <strong>dummy air ticket</strong> from MyDummyTicket, you get a legitimate flight reservation that satisfies embassy requirements without the financial risk. Our <strong>dummy flight tickets</strong> include real airline names, flight numbers, departure and arrival times, and most importantly — a genuine PNR code that can be independently verified.
            </p>
            <p>
              Whether you're applying for a <strong>Schengen visa</strong>, <strong>UK visitor visa</strong>, <strong>US B1/B2 visa</strong>, <strong>Canada visitor visa</strong>, or any other travel document — a <strong>dummy booking</strong> from our platform meets all documentation requirements. Immigration officers at airports also accept our <strong>dummy itinerary</strong> as valid proof of onward travel, making it essential for digital nomads and one-way travellers.
            </p>
          </div>
        </div>

        {/* Section 3: How Our Dummy Ticket Service Works */}
        <div>
          <div className="section-header">
            <div className="eyebrow">Simple Process</div>
            <h2 className="section-title" style={{ fontSize: '28px' }}>How Our Dummy Ticket Service Works</h2>
          </div>
          <div style={{ fontSize: '15px', lineHeight: '1.8', color: '#374151' }}>
            <p style={{ marginBottom: '16px' }}>
              Getting a <strong>dummy ticket</strong> from MyDummyTicket is fast, simple, and secure. Our <strong>dummy ticket</strong> service works in three easy steps: First, enter your travel details — departure city, destination, travel dates, and passenger information. Second, choose your ticket type — our Standard <strong>dummy flight ticket</strong> (from {format(6.99)}) includes an auto-generated reservation, while our Verifiable <strong>dummy air ticket</strong> (from {format(11.99)}) comes with a real airline PNR.
            </p>
            <p style={{ marginBottom: '16px' }}>
              Third, complete your secure payment via Stripe and receive your <strong>dummy booking</strong> confirmation instantly via email. Standard <strong>dummy tickets</strong> are delivered within 1 minute as a professional PDF. Verifiable PNR <strong>dummy flight bookings</strong> are processed within 1 minute to 6 hours, depending on airline availability. Every <strong>dummy ticket</strong> we generate includes the airline logo, flight details, passenger information, and booking reference.
            </p>
            <p>
              Our <strong>dummyticket</strong> service is a UK-registered business (ICO Reg: ZC006858) with secure Stripe payments, 24/7 customer support, and a 4.7-star Trustpilot rating. Whether you search for <strong>dummy ticket</strong>, <strong>ticket dummy</strong>, <strong>dummy flight ticket</strong>, or <strong>dummy air ticket</strong> — MyDummyTicket is the trusted choice for visa applicants and travellers worldwide. Order your <strong>dummy ticket</strong> today and get your visa application moving.
            </p>
          </div>

          {/* CTA */}
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/order" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block', fontSize: '16px', padding: '16px 36px' }} data-testid="explainer-cta-btn">
              Get Your Dummy Ticket Now — From {format(6.99)}
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
};

// Latest Visa Guides Section - Internal Linking from Homepage to Blog
const LatestVisaGuidesSection = () => {
  const guides = [
    { slug: 'schengen-visa-flight-reservation-guide', title: 'Flight Reservation for Schengen Visa: Complete Application Guide', anchor: 'Schengen visa flight reservation guide', category: 'Schengen Visa' },
    { slug: 'what-is-dummy-ticket-visa-application', title: 'What is a Dummy Ticket? Everything You Need to Know', anchor: 'What is a dummy ticket for visa?', category: 'Visa Guide' },
    { slug: 'proof-of-onward-travel-immigration', title: 'Proof of Onward Travel for Immigration: Complete Guide 2026', anchor: 'Proof of onward travel guide', category: 'Travel Guide' },
    { slug: 'dummy-ticket-for-schengen-visa-guide', title: 'Dummy Ticket for Schengen Visa: Complete 2026 Guide', anchor: 'Dummy ticket for Schengen visa', category: 'Visa Guide' },
    { slug: 'dummy-ticket-for-us-visa', title: 'Dummy Ticket for US Visa: B1/B2 Application Strategy Guide', anchor: 'Dummy ticket for US visa application', category: 'Visa Guide' },
    { slug: 'dummy-ticket-for-canada-visa', title: 'Dummy Ticket for Canada Visa: Visitor Visa Application Guide', anchor: 'Dummy ticket for Canada visa', category: 'Visa Guide' }
  ];

  return (
    <section className="section" style={{ background: '#f0fdfa' }}>
      <div className="container" style={{ maxWidth: '1100px' }}>
        <div className="section-header">
          <div className="eyebrow">Expert Visa Guides</div>
          <h2 className="section-title" style={{ fontSize: '28px' }}>Latest Visa & Dummy Ticket Guides</h2>
          <p style={{ color: '#5f717e', maxWidth: '600px', margin: '0 auto', fontSize: '15px' }}>
            Expert guides on dummy tickets, flight reservations for visa applications, and proof of onward travel requirements.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginTop: '32px' }}>
          {guides.map((guide, i) => (
            <Link key={i} to={`/blog/${guide.slug}`} data-testid={`visa-guide-link-${i}`} style={{
              background: 'white', padding: '24px', borderRadius: '12px', textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0',
              transition: 'box-shadow 0.2s, transform 0.2s', display: 'block'
            }}>
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#0f766e', background: '#f0fdfa', padding: '4px 10px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {guide.category}
              </span>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e3a5f', margin: '12px 0 8px', lineHeight: '1.4' }}>
                {guide.anchor}
              </h3>
              <p style={{ fontSize: '13px', color: '#5f717e', lineHeight: '1.5', margin: 0 }}>
                {guide.title}
              </p>
            </Link>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '32px', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/visa-guides" data-testid="visa-guides-hub-link" style={{ color: '#0f766e', fontWeight: '600', fontSize: '15px', textDecoration: 'none', padding: '12px 24px', border: '2px solid #0f766e', borderRadius: '10px' }}>
            View All Visa Guides
          </Link>
          <Link to="/blog" style={{ color: '#5f717e', fontWeight: '500', fontSize: '15px', textDecoration: 'none', padding: '12px 24px' }}>
            Browse All Articles →
          </Link>
        </div>
      </div>
    </section>
  );
};

const TrackOrderSection = () => {
  const { t } = useLanguage();
  const [trackId, setTrackId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async () => {
    if (!trackId.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      // Try PNR first
      let response = await axios.get(`${API}/bookings/pnr/${trackId}`).catch(() => null);
      if (!response?.data?.booking) {
        // Try booking ID
        response = await axios.get(`${API}/bookings/${trackId}`);
      }
      setResult(response.data.booking);
    } catch (err) {
      setError('Order not found. Please check your Order ID or PNR.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section" id="track">
      <div className="container" style={{ maxWidth: '600px' }}>
        <div className="section-header">
          <div className="eyebrow">Live Tracking</div>
          <h2 className="section-title">{t.track.title}</h2>
          <p className="section-desc">{t.track.subtitle}</p>
        </div>

        <div className="form-card" style={{ padding: '32px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              className="form-input"
              placeholder={t.track.placeholder}
              value={trackId}
              onChange={(e) => setTrackId(e.target.value.toUpperCase())}
              style={{ flex: 1 }}
              data-testid="track-input"
            />
            <button 
              className="btn-primary" 
              style={{ padding: '14px 28px' }} 
              onClick={handleTrack}
              disabled={loading}
              data-testid="track-submit"
            >
              {loading ? t.track.searching : t.track.button}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#fee2e2', borderRadius: '8px', color: '#dc2626', fontSize: '14px' }}>
              {error}
            </div>
          )}

          {result && (
            <div style={{ marginTop: '24px', padding: '20px', background: 'var(--bg-primary)', borderRadius: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontWeight: '700', fontSize: '18px' }}>{t.orders.orderNumber}{result.order_number}</span>
                <span style={{ 
                  padding: '6px 12px', 
                  borderRadius: '20px', 
                  fontSize: '12px', 
                  fontWeight: '700',
                  background: result.payment_status === 'paid' ? '#d1fae5' : '#fef3c7',
                  color: result.payment_status === 'paid' ? '#059669' : '#d97706'
                }}>
                  {result.payment_status === 'paid' ? '✓ PAID' : '⏳ PENDING'}
                </span>
              </div>
              {result.pnr_code && (
                <div style={{ marginBottom: '12px' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>PNR Code</span>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--accent-teal)', fontFamily: 'monospace' }}>{result.pnr_code}</div>
                </div>
              )}
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                {result.departure_airport} → {result.arrival_airport}<br/>
                {result.first_name} {result.last_name}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Standalone Track Order Page
const TrackOrderPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [trackId, setTrackId] = useState(searchParams.get('id') || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useSEO(
    'Track Your Dummy Ticket Order | Check PNR Status | MyDummyTicket',
    'Track your dummy ticket order status using your PNR code or order number. Verify your flight reservation for visa application, download PDF itinerary, and check booking confirmation instantly.'
  );

  useEffect(() => {
    // Set additional meta tags for SEO
    const setMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setMetaTag('keywords', 'track dummy ticket, check PNR status, verify flight reservation, dummy ticket order status, flight itinerary for visa, PNR verification, booking confirmation, track flight reservation');
    
    // Auto-search if ID is provided in URL
    if (searchParams.get('id')) {
      handleTrack();
    }
  }, []);

  const handleTrack = async () => {
    if (!trackId.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      let response = await axios.get(`${API}/bookings/pnr/${trackId}`).catch(() => null);
      if (!response?.data?.booking) {
        response = await axios.get(`${API}/bookings/${trackId}`);
      }
      setResult(response.data.booking);
    } catch (err) {
      setError('Order not found. Please check your Order ID or PNR code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '120px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '650px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px' }}>Track Your Dummy Ticket Order</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Enter your order number or PNR code to check status and download your flight reservation</p>
        </div>

        {/* SEO Content Section */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid var(--border-light)' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>How to Track Your Flight Reservation</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '12px' }}>
            Easily verify your <strong>dummy ticket for visa application</strong> using your unique PNR code or order number. 
            Our tracking system allows you to check your <strong>flight itinerary status</strong>, download your PDF confirmation, 
            and verify your booking is valid for <strong>Schengen visa</strong>, UK visa, US visa, or any immigration requirement.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ padding: '4px 10px', background: '#f0fdf4', color: '#166534', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>✓ Instant PNR Verification</span>
            <span style={{ padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>✓ PDF Download</span>
            <span style={{ padding: '4px 10px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>✓ Real-time Status</span>
          </div>
        </div>

        <div className="form-card" style={{ padding: '32px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Order ID or PNR Code"
              value={trackId}
              onChange={(e) => setTrackId(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
              style={{ flex: 1 }}
              data-testid="track-input"
            />
            <button 
              className="btn-primary" 
              style={{ padding: '14px 28px' }} 
              onClick={handleTrack}
              disabled={loading}
              data-testid="track-submit"
            >
              {loading ? 'Searching...' : 'Track'}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: '16px', padding: '14px', background: '#fee2e2', borderRadius: '10px', color: '#dc2626', fontSize: '14px' }}>
              {error}
            </div>
          )}

          {result && (
            <div style={{ marginTop: '24px' }}>
              <div style={{ background: '#d1fae5', padding: '16px', borderRadius: '10px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '700', color: '#059669' }}>✓ Order Found</span>
                  <span style={{ 
                    padding: '6px 14px', 
                    borderRadius: '20px', 
                    fontSize: '12px', 
                    fontWeight: '700',
                    background: result.payment_status === 'paid' ? '#059669' : '#d97706',
                    color: 'white'
                  }}>
                    {result.payment_status === 'paid' ? 'PAID' : 'PENDING'}
                  </span>
                </div>
              </div>

              <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
                <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-light)' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>ORDER NUMBER</div>
                  <div style={{ fontSize: '18px', fontWeight: '800', fontFamily: 'monospace' }}>{result.order_number}</div>
                </div>
                
                <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-light)' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>PNR CODE</div>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: 'var(--accent-teal)', fontFamily: 'monospace' }}>{result.pnr_code}</div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>ROUTE</div>
                    <div style={{ fontWeight: '600' }}>{result.departure_airport} → {result.arrival_airport}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>PASSENGER</div>
                    <div style={{ fontWeight: '600' }}>{result.first_name} {result.last_name}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>TRAVEL DATE</div>
                    <div style={{ fontWeight: '600' }}>{result.travel_date}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>DOCUMENT TYPE</div>
                    <div style={{ fontWeight: '600', color: 'var(--accent-teal)' }}>
                      {result.document_type === 'verifiable' ? '🛡️ Verifiable PNR' : '📄 Standard'}
                    </div>
                  </div>
                </div>
              </div>
              
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '16px', textAlign: 'center' }}>
                Need help? Contact <a href="mailto:order@mydummyticket.com" style={{ color: 'var(--accent-teal)' }}>order@mydummyticket.com</a>
              </p>
            </div>
          )}
        </div>

        {/* Trust Badges */}
        <TrustBadgesInline />
      </div>
    </div>
  );
};

// Cookie Consent Banner - GDPR Compliant
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a small delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  // Send consent to backend for tracking
  const trackConsent = async (prefs) => {
    try {
      await axios.post(`${API}/cookies/consent`, {
        essential: prefs.essential,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        user_agent: navigator.userAgent
      });
    } catch (e) {
      console.log('Cookie consent tracking failed');
    }
  };

  const acceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    trackConsent(allAccepted);
  };

  const acceptEssential = () => {
    const essentialOnly = { essential: true, analytics: false, marketing: false };
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    setPreferences(essentialOnly);
    setShowBanner(false);
    trackConsent(essentialOnly);
  };

  const savePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
    trackConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'white',
      boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
      zIndex: 9999,
      padding: '20px 0',
      borderTop: '3px solid var(--accent-teal)'
    }}>
      <div className="container" style={{ maxWidth: '1200px' }}>
        {!showSettings ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: '24px' }}>🍪</span>
                <h4 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>We value your privacy</h4>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.6' }}>
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                By clicking "Accept All", you consent to our use of cookies. 
                <a href="/privacy" style={{ color: 'var(--accent-teal)', marginLeft: '4px' }}>Privacy Policy</a>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => setShowSettings(true)}
                style={{
                  padding: '10px 20px',
                  border: '2px solid var(--border-light)',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Customize
              </button>
              <button 
                onClick={acceptEssential}
                style={{
                  padding: '10px 20px',
                  border: '2px solid var(--accent-teal)',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--accent-teal)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Essential Only
              </button>
              <button 
                onClick={acceptAll}
                className="btn-primary"
                style={{
                  padding: '10px 24px',
                  fontSize: '13px'
                }}
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>🍪 Cookie Preferences</h4>
              <button 
                onClick={() => setShowSettings(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                ×
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
              {/* Essential Cookies */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>🔒 Essential Cookies</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Required for the website to function. Cannot be disabled.</div>
                </div>
                <div style={{ 
                  padding: '4px 12px', 
                  background: '#dcfce7', 
                  color: '#166534', 
                  borderRadius: '20px', 
                  fontSize: '11px', 
                  fontWeight: '600' 
                }}>
                  Always On
                </div>
              </div>

              {/* Analytics Cookies */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>📊 Analytics Cookies</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Help us understand how visitors interact with our website.</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(p => ({...p, analytics: e.target.checked}))}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: preferences.analytics ? 'var(--accent-teal)' : '#ccc',
                    borderRadius: '26px',
                    transition: '0.3s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '',
                      height: '20px',
                      width: '20px',
                      left: preferences.analytics ? '24px' : '3px',
                      bottom: '3px',
                      background: 'white',
                      borderRadius: '50%',
                      transition: '0.3s'
                    }}></span>
                  </span>
                </label>
              </div>

              {/* Marketing Cookies */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>📢 Marketing Cookies</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Used to deliver relevant advertisements and track campaign effectiveness.</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                  <input 
                    type="checkbox" 
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(p => ({...p, marketing: e.target.checked}))}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: preferences.marketing ? 'var(--accent-teal)' : '#ccc',
                    borderRadius: '26px',
                    transition: '0.3s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '',
                      height: '20px',
                      width: '20px',
                      left: preferences.marketing ? '24px' : '3px',
                      bottom: '3px',
                      background: 'white',
                      borderRadius: '50%',
                      transition: '0.3s'
                    }}></span>
                  </span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                onClick={acceptEssential}
                style={{
                  padding: '10px 20px',
                  border: '2px solid var(--border-light)',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Reject All
              </button>
              <button 
                onClick={savePreferences}
                className="btn-primary"
                style={{
                  padding: '10px 24px',
                  fontSize: '13px'
                }}
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [totalVisitors, setTotalVisitors] = useState(50000);
  
  useEffect(() => {
    // Fetch total visitors count
    const fetchTotalVisitors = async () => {
      try {
        const res = await axios.get(`${API}/analytics/total-visitors`);
        if (res.data?.total_visitors) {
          setTotalVisitors(res.data.total_visitors);
        }
      } catch (err) {
        // Keep default value
      }
    };
    fetchTotalVisitors();
  }, []);
  
  const scrollTo = (id) => {
    navigate('/');
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  return (
    <footer className="footer">
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '40px', marginBottom: '40px' }}>
          <div>
            <div 
              style={{ marginBottom: '20px', cursor: 'pointer' }} 
              onClick={() => { 
                if (window.location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  navigate('/');
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }
              }}
              data-testid="footer-logo"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ticket-system-89/artifacts/85tijzvo_MYDUMMYTICKET-AI.svg" 
                alt="My Dummy Ticket - Verifiable Flight Reservations for Visa Applications" 
                style={{ height: '52px' }}
                width="180"
                height="52"
                loading="lazy"
              />
            </div>
            <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.8)', lineHeight: '1.7' }}>
              {t.footer.tagline}
            </p>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', marginTop: '12px' }}>
              Trusted flight reservation service for visa applications, proof of onward travel, and immigration requirements worldwide.
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px' }}>Navigation</h3>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <li><a href="/#how" onClick={(e) => { e.preventDefault(); scrollTo('how'); }} style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px', cursor: 'pointer' }}>🎯 {t.nav.howItWorks}</a></li>
              <li><a href="/#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }} style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px', cursor: 'pointer' }}>💰 {t.nav.pricing}</a></li>
              <li><a href="/faq" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>❓ {t.nav.faq}</a></li>
            </ul>
          </div>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px' }}>{t.footer.quickLinks}</h3>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <li><a href="/free-resources" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>Free Resources</a></li>
              <li><a href="/templates" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>{t.nav.templates}</a></li>
              <li><a href="/visa-guides" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>Visa Guides</a></li>
              <li><a href="/blog" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>Blog</a></li>
              <li><a href="https://myonwardticket.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>Best Onward Ticket</a></li>
            </ul>
          </div>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px' }}>Dummy Tickets By Visa</h3>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <li><a href="/schengen-visa-flight-reservation" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>Schengen Visa Flight Reservation</a></li>
              <li><a href="/uk-visa-flight-reservation" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>UK Visa Flight Reservation</a></li>
              <li><a href="/canada-visa-flight-reservation" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>Canada Visa Flight Reservation</a></li>
              <li><a href="/thailand-onward-ticket" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>Thailand Onward Ticket</a></li>
              <li><a href="/dummy-ticket-india" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>Dummy Ticket India</a></li>
              <li><a href="/dummy-ticket-nigeria" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>Dummy Ticket Nigeria</a></li>
              <li><a href="/dubai-visa-flight-reservation" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>Dubai/UAE Visa Flight Reservation</a></li>
              <li><a href="/us-visa-flight-reservation" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>US Visa Flight Reservation</a></li>
              <li><a href="/india-visa-flight-reservation" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px' }}>India Visa Flight Reservation</a></li>
            </ul>
          </div>
          <div data-testid="footer-schengen-countries">
            <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px' }}>Schengen Visa by Country</h3>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <li><a href="/flight-reservation-for-france-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇫🇷</span> France Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-germany-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇩🇪</span> Germany Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-italy-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇮🇹</span> Italy Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-spain-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇪🇸</span> Spain Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-netherlands-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇳🇱</span> Netherlands Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-belgium-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇧🇪</span> Belgium Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-switzerland-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇨🇭</span> Switzerland Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-austria-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇦🇹</span> Austria Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-greece-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇬🇷</span> Greece Visa Flight Reservation</a></li>
              <li><a href="/flight-reservation-for-portugal-visa" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontSize: '18px', lineHeight: 1 }}>🇵🇹</span> Portugal Visa Flight Reservation</a></li>
            </ul>
          </div>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px' }}>{t.footer.legal}</h3>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <li><a href="/privacy" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>🔒 {t.footer.privacy}</a></li>
              <li><a href="/terms" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>📋 {t.footer.terms}</a></li>
              <li><a href="/terms#refund" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>💰 {t.footer.refund}</a></li>
            </ul>
          </div>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px' }}>{t.footer.support}</h3>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <li>
                <a href="https://t.me/mydummyticket_bot" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                  <span>Order on Telegram</span>
                </a>
              </li>
              <li><a href="/contact" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>📞 {t.footer.contact}</a></li>
              <li><a href="/feedback" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>💭 Feedback</a></li>
            </ul>
          </div>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px' }}>Partners</h3>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <li><a href="/agent" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>👔 Become an Agent</a></li>
              <li><a href="/affiliate" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '16px' }}>💰 Affiliate Program</a></li>
            </ul>
          </div>
        </div>
        
        {/* Payment Methods - Highly Visible with Images */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          gap: '16px', 
          padding: '28px 32px',
          borderTop: '1px solid rgba(255,255,255,0.15)',
          marginTop: '24px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '16px',
          margin: '24px 0'
        }}>
          <span style={{ fontSize: '14px', color: 'white', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Secure Payments</span>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* Stripe */}
            <div style={{ background: 'white', padding: '10px 18px', borderRadius: '8px' }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" style={{ height: '24px' }} loading="lazy" width="60" height="24" />
            </div>
            {/* Visa */}
            <div style={{ background: 'white', padding: '10px 18px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg viewBox="0 0 750 471" height="18" aria-label="Visa">
                <path d="M278.2 334.2l33.7-194.8h53.9L332 334.2h-53.8zm246.8-189.8c-10.7-4-27.4-8.3-48.3-8.3-53.3 0-90.8 26.6-91.1 64.8-.3 28.2 26.8 43.9 47.2 53.3 21 9.6 28 15.7 27.9 24.3-.1 13.1-16.7 19.1-32.2 19.1-21.5 0-32.9-3-50.6-10.2l-6.9-3.1-7.5 43.6c12.6 5.4 35.8 10.1 59.9 10.4 56.7 0 93.5-26.3 93.9-67.1.2-22.4-14.2-39.4-45.4-53.4-18.9-9.1-30.5-15.2-30.4-24.4 0-8.2 9.8-16.9 31-16.9 17.7-.3 30.5 3.5 40.5 7.5l4.9 2.3 7.4-41.9h-.3zm140.4-5l-41.8 0c-12.9 0-22.6 3.5-28.3 16.3L497.3 334.2h56.6s9.3-24.1 11.3-29.4c6.2 0 61.1.1 68.9.1 1.6 6.8 6.5 29.3 6.5 29.3h50l-43.6-194.7h-.6zm-66 125.8c4.5-11.3 21.5-54.8 21.5-54.8-.3.5 4.4-11.3 7.1-18.7l3.6 16.9s10.3 46.9 12.5 56.6H599.4zm-390.5-125.8l-52.8 132.7-5.6-27.2c-9.8-31.2-40.3-65.1-74.5-82l48.3 171.4 57.1-.1 84.9-194.8h-57.4z" fill="#1434CB"/>
                <path d="M146.9 139.4H60.6l-.7 3.9c67.7 16.2 112.5 55.4 131.1 102.5l-18.9-90c-3.3-12.4-12.9-15.9-25.2-16.4z" fill="#F9A533"/>
              </svg>
            </div>
            {/* Mastercard */}
            <div style={{ background: 'white', padding: '10px 18px', borderRadius: '8px' }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" style={{ height: '28px' }} loading="lazy" width="45" height="28" />
            </div>
            {/* PayPal */}
            <div style={{ background: 'white', padding: '10px 18px', borderRadius: '8px' }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" style={{ height: '22px' }} loading="lazy" width="85" height="22" />
            </div>
            {/* Amex */}
            <div style={{ background: '#006FCF', padding: '10px 18px', borderRadius: '8px' }}>
              <span style={{ color: 'white', fontWeight: '800', fontSize: '16px', letterSpacing: '1px' }}>AMEX</span>
            </div>
          </div>
        </div>
        
        {/* Trust Badges - ICO & Made in Britain */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '50px', 
          padding: '28px 0',
          borderTop: '1px solid rgba(255,255,255,0.15)',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_e28954c1-fe1f-4ffb-a843-f92e2904e55a/artifacts/lggpfw5z_ICO_Welsh_Logo_WhiteOnBlue.jpg" 
              alt="ICO - Information Commissioner's Office" 
              style={{ height: '56px', width: '56px', borderRadius: '10px', display: 'block', objectFit: 'cover' }}
              loading="lazy"
            />
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', maxWidth: '140px', lineHeight: '1.5', fontWeight: '500' }}>
              ICO Registered<br/>
              <span style={{ opacity: 0.7, fontSize: '11px' }}>Reg: ZC006858</span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ background: 'white', borderRadius: '10px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_e28954c1-fe1f-4ffb-a843-f92e2904e55a/artifacts/9qcabmmw_Logo-1181x600-%20madd%20in%20britain_jpg.jpg" 
                alt="Made in Britain" 
                style={{ height: '42px', display: 'block' }}
                loading="lazy"
              />
            </div>
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', maxWidth: '100px', lineHeight: '1.5', fontWeight: '500' }}>
              Proudly British<br/>
              <span style={{ opacity: 0.7 }}>Company</span>
            </span>
          </div>
        </div>
        
        <div style={{ textAlign: 'center', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>👥 Total Visitors:</span>
            <span style={{ color: '#22c55e', fontWeight: '700', fontSize: '16px' }}>{totalVisitors.toLocaleString()}+</span>
          </div>
          <div style={{ marginBottom: '8px' }}>
            © {new Date().getFullYear()} My Dummy Ticket. {t.footer.allRightsReserved}
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '12px', color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
          {t.footer.disclaimer}
        </div>
      </div>
    </footer>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [booking, setBooking] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Fetch booking details
          if (data.metadata?.booking_id) {
            const bookingRes = await axios.get(`${API}/bookings/${data.metadata.booking_id}`);
            setBooking(bookingRes.data.booking);
          }
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  const handleDownloadPDF = async () => {
    if (!booking?.id) return;
    try {
      const response = await axios.get(`${API}/bookings/${booking.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FlightReservation_${booking.pnr_code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'linear-gradient(180deg, #f0fdfa 0%, #e8f4f8 100%)' }}>
      <div className="container" style={{ maxWidth: '560px' }}>
        
        {status === 'checking' && (
          <div style={{ background: 'white', borderRadius: '24px', padding: '48px 32px', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <div style={{ width: '72px', height: '72px', margin: '0 auto 24px', position: 'relative' }}>
              <div style={{ 
                width: '72px', height: '72px', border: '4px solid #e2e8f0', borderTopColor: '#0f766e', 
                borderRadius: '50%', animation: 'spin 1s linear infinite'
              }}></div>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', color: '#0f172a' }}>Verifying Payment...</h2>
            <p style={{ color: '#5f717e', fontSize: '15px' }}>Please wait while we confirm your payment</p>
          </div>
        )}

        {status === 'success' && booking && (
          <div style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            {/* Success Header */}
            <div style={{ 
              background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', 
              padding: '40px 32px', 
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{ 
                width: '80px', height: '80px', 
                background: 'white', 
                borderRadius: '50%', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                margin: '0 auto 20px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
              }}>
                <span style={{ fontSize: '40px' }}>✓</span>
              </div>
              <h2 style={{ color: 'white', fontSize: '26px', fontWeight: '800', margin: '0 0 8px 0' }}>Payment Successful!</h2>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', margin: 0 }}>
                {booking.document_type === 'verifiable' 
                  ? (booking.include_hotel 
                      ? 'Your verifiable flight + hotel booking is being prepared' 
                      : 'Your verifiable ticket is being prepared')
                  : (booking.include_hotel 
                      ? 'Your flight + hotel reservation is confirmed' 
                      : 'Your flight reservation is confirmed')}
              </p>
            </div>
            
            {/* PNR Section */}
            <div style={{ padding: '32px', borderBottom: '1px dashed #e2e8f0' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#5f717e', fontWeight: '600', letterSpacing: '1px', marginBottom: '8px' }}>YOUR BOOKING REFERENCE (PNR)</div>
                <div style={{ 
                  fontSize: '38px', fontWeight: '800', color: '#0f766e', 
                  fontFamily: 'monospace', letterSpacing: '6px',
                  background: '#f0fdfa', padding: '16px 24px', borderRadius: '12px',
                  display: 'inline-block'
                }} data-testid="pnr-code">{booking.pnr_code}</div>
              </div>
            </div>
            
            {/* Booking Details */}
            <div style={{ padding: '24px 32px' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#5f717e', fontSize: '14px' }}>Order Number</span>
                  <span style={{ fontWeight: '700', fontSize: '14px', fontFamily: 'monospace' }} data-testid="order-number">{booking.order_number}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#5f717e', fontSize: '14px' }}>Passenger</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{booking.first_name} {booking.last_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#5f717e', fontSize: '14px' }}>Route</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{booking.departure_airport} → {booking.arrival_airport}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#5f717e', fontSize: '14px' }}>Travel Date</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{booking.departure_date}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#5f717e', fontSize: '14px' }}>Document Type</span>
                  <span style={{ 
                    fontWeight: '600', fontSize: '13px', 
                    background: booking.document_type === 'verifiable' ? '#f0fdfa' : '#fef3c7',
                    color: booking.document_type === 'verifiable' ? '#0f766e' : '#92400e',
                    padding: '4px 10px', borderRadius: '6px'
                  }}>
                    {booking.document_type === 'verifiable' ? '🛡️ Verifiable PNR' : '📄 Standard'}
                  </span>
                </div>
                {/* Hotel Info if included */}
                {booking.include_hotel && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#5f717e', fontSize: '14px' }}>Hotel</span>
                    <span style={{ fontWeight: '600', fontSize: '13px', color: '#0f766e' }}>
                      🏨 {booking.selected_hotel?.name || booking.hotel_destination || 'Included'}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#5f717e', fontSize: '14px' }}>Amount Paid</span>
                  <span style={{ fontWeight: '800', fontSize: '20px', color: '#0f766e' }}>£{booking.price}</span>
                </div>
              </div>
            </div>
            
            {/* Delivery Notice - Different for Standard vs Verifiable */}
            {booking.document_type === 'verifiable' ? (
              <div style={{ margin: '0 32px 24px' }}>
                {/* Verifiable PNR - Different handling for bundle vs flight only */}
                {booking.include_hotel ? (
                  <>
                    {/* Hotel PDF - Delivered Instantly */}
                    <div style={{ background: '#dcfce7', padding: '16px', borderRadius: '10px', border: '1px solid #86efac', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px' }}>🏨</span>
                        <span style={{ fontSize: '14px', color: '#166534', fontWeight: '700' }}>Hotel Booking - Delivered ✓</span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#166534', margin: 0, lineHeight: '1.5' }}>
                        Your <strong>Hotel Booking PDF</strong> for <strong>{booking.hotel_destination || booking.arrival_airport}</strong> has been sent to <strong>{booking.email}</strong>.
                        {booking.selected_hotel?.name && <span> ({booking.selected_hotel.name})</span>}
                      </p>
                    </div>
                    
                    {/* Flight Ticket - Manual Processing */}
                    <div style={{ background: '#dbeafe', padding: '16px', borderRadius: '10px', border: '1px solid #93c5fd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px' }}>✈️</span>
                        <span style={{ fontSize: '14px', color: '#1e40af', fontWeight: '700' }}>Flight Ticket - Processing</span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
                        Your <strong>Verifiable PNR flight ticket</strong> will be delivered within <strong>1 minute to 6 hours</strong>. 
                        Our team is preparing your authentic airline reservation.
                      </p>
                    </div>
                  </>
                ) : (
                  /* Verifiable PNR only - no hotel */
                  <div style={{ background: '#dbeafe', padding: '16px', borderRadius: '10px', border: '1px solid #93c5fd' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '20px' }}>⏱️</span>
                      <span style={{ fontSize: '14px', color: '#1e40af', fontWeight: '700' }}>Manual Processing Required</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
                      Your <strong>Verifiable PNR ticket</strong> will be delivered to <strong>{booking.email}</strong> within <strong>1 minute to 6 hours</strong>. 
                      Our team is preparing your authentic airline reservation with a real PNR code.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Standard Ticket - Instant Delivery */
              <div style={{ margin: '0 32px 24px' }}>
                {/* Main delivery notice */}
                <div style={{ background: '#dcfce7', padding: '16px', borderRadius: '10px', border: '1px solid #86efac', marginBottom: booking.include_hotel ? '12px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>✅</span>
                    <span style={{ fontSize: '14px', color: '#166534', fontWeight: '700' }}>Instant Delivery</span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#166534', margin: 0, lineHeight: '1.5' }}>
                    Your <strong>Flight Ticket PDF</strong> has been sent to <strong>{booking.email}</strong> within <strong>1 minute</strong>.
                  </p>
                </div>
                
                {/* Hotel Bundle Notice for Standard */}
                {booking.include_hotel && (
                  <div style={{ background: '#fef3c7', padding: '16px', borderRadius: '10px', border: '1px solid #fcd34d' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '20px' }}>🏨</span>
                      <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '700' }}>Hotel Booking Included</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.5' }}>
                      Your <strong>Hotel Booking PDF</strong> for <strong>{booking.hotel_destination || booking.arrival_airport}</strong> has also been sent to your email.
                      {booking.selected_hotel?.name && <span> ({booking.selected_hotel.name})</span>}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Action Buttons */}
            <div style={{ padding: '0 32px 32px' }}>
              {/* Download buttons for Standard tickets (instant delivery) */}
              {booking.document_type !== 'verifiable' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                  <button 
                    onClick={handleDownloadPDF}
                    style={{ 
                      width: '100%', padding: '16px', 
                      background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', 
                      color: 'white', border: 'none', borderRadius: '12px', 
                      fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
                    }}
                    data-testid="download-pdf-btn"
                  >
                    ✈️ Download Flight Ticket PDF
                  </button>
                  
                  {/* Hotel PDF Download for Standard Bundle */}
                  {booking.include_hotel && (
                    <button 
                      onClick={async () => {
                        if (!booking?.id) return;
                        try {
                          const response = await axios.get(`${API}/bookings/${booking.id}/hotel-pdf`, {
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `HotelBooking_${booking.order_number}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch (error) {
                          console.error('Error downloading hotel PDF:', error);
                          alert('Hotel PDF will be sent to your email shortly.');
                        }
                      }}
                      style={{ 
                        width: '100%', padding: '16px', 
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                        color: 'white', border: 'none', borderRadius: '12px', 
                        fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
                      }}
                      data-testid="download-hotel-pdf-btn"
                    >
                      🏨 Download Hotel Booking PDF
                    </button>
                  )}
                </div>
              )}
              
              {/* For verifiable, show a different message */}
              {booking.document_type === 'verifiable' && (
                <div style={{ 
                  background: '#f0fdf4', 
                  padding: '16px', 
                  borderRadius: '12px', 
                  marginBottom: '12px',
                  border: '1px solid #86efac',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '14px', color: '#166534' }}>
                    📩 Your {booking.include_hotel ? 'flight ticket and hotel booking PDFs' : 'ticket PDF'} will be emailed once processed (1 min - 6 hours)
                  </span>
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                <button 
                  onClick={() => navigate('/orders')}
                  style={{ 
                    padding: '14px 32px', background: 'white', color: '#0f766e', 
                    border: '2px solid #0f766e', borderRadius: '10px', 
                    fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                  }}
                  data-testid="view-orders-btn"
                >
                  📋 View My Orders
                </button>
              </div>
              
              <button 
                onClick={() => navigate('/')}
                style={{ width: '100%', padding: '12px', background: 'transparent', color: '#5f717e', border: 'none', fontSize: '14px', cursor: 'pointer' }}
                data-testid="back-home"
              >
                ← Back to Home
              </button>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div style={{ background: 'white', borderRadius: '24px', padding: '48px 32px', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <div style={{ 
              width: '80px', height: '80px', background: '#fee2e2', borderRadius: '50%', 
              display: 'flex', alignItems: 'center', justifyContent: 'center', 
              margin: '0 auto 24px', fontSize: '36px'
            }}>❌</div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px', color: '#dc2626' }}>Payment Issue</h2>
            <p style={{ color: '#5f717e', marginBottom: '16px', fontSize: '15px' }}>
              {status === 'expired' ? 'Your payment session has expired.' : 'There was an issue verifying your payment.'}
            </p>
            <p style={{ fontSize: '13px', color: '#6b7a85', marginBottom: '24px' }}>
              If you were charged, please contact <a href="mailto:order@mydummyticket.com" style={{ color: '#0f766e' }}>order@mydummyticket.com</a>
            </p>
            <button 
              onClick={() => navigate('/order')} 
              style={{ 
                width: '100%', padding: '16px', 
                background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', 
                color: 'white', border: 'none', borderRadius: '12px', 
                fontSize: '15px', fontWeight: '700', cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};


// Home Page
// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};


// Popular visa flight reservation pages — homepage internal links for SEO
const PopularVisaPagesSection = () => {
  const pages = [
    { url: '/schengen-visa-flight-reservation', flag: '🇪🇺', label: 'Schengen Visa' },
    { url: '/uk-visa-flight-reservation', flag: '🇬🇧', label: 'UK Visa' },
    { url: '/us-visa-flight-reservation', flag: '🇺🇸', label: 'US B1/B2 Visa' },
    { url: '/canada-visa-flight-reservation', flag: '🇨🇦', label: 'Canada Visa' },
    { url: '/dubai-visa-flight-reservation', flag: '🇦🇪', label: 'UAE / Dubai Visa' },
    { url: '/india-visa-flight-reservation', flag: '🇮🇳', label: 'India Visa' },
    { url: '/flight-reservation-for-greece-visa', flag: '🇬🇷', label: 'Greece Visa' },
    { url: '/flight-reservation-for-spain-visa', flag: '🇪🇸', label: 'Spain Visa' },
  ];
  return (
    <section style={{ background: 'white', padding: '56px 20px', borderTop: '1px solid #e2e8f0' }} data-testid="popular-visa-pages">
      <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a5f', marginBottom: '8px' }}>
          Flight Reservations for Every Visa Application
        </h2>
        <p style={{ fontSize: '14px', color: '#5f717e', marginBottom: '28px' }}>
          Country-specific guides with embassy requirements, checklists and verified dummy tickets from £6.99
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px' }}>
          {pages.map((p, i) => (
            <Link key={i} to={p.url} data-testid={`visa-link-${p.url.replace(/\//g, '')}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 20px', background: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: '12px', color: '#0f766e', fontWeight: '600', fontSize: '14px', textDecoration: 'none' }}>
              <span style={{ fontSize: '18px' }}>{p.flag}</span> {p.label} Flight Reservation
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const HomePage = ({ pricing, faqData }) => {
  const navigate = useNavigate();
  
  useSEO(
    'Dummy Ticket for Visa | Verified Flight Reservations | MyDummyTicket',
    'Get verified dummy tickets for visa applications. Real PNRs, CheckMyTrip verified, embassy accepted. Dummy ticket, dummy flight ticket, dummy air ticket, dummy booking. Instant delivery from £6.99. Order now!',
    'dummy ticket, dummyticket, my dummy ticket, mydummyticket, dummy ticket for visa, dummy flight ticket, dummy flight booking, dummy air ticket, ticket dummy, dummy itinerary, dummy booking, dummy ticket online, flight reservation for visa application, proof of onward travel, verifiable PNR code, dummy airline ticket'
  );

  const handleSelectPlan = (type, validity, tripType = 'one_way') => {
    // Navigate to order page with selected plan details
    navigate(`/order?type=${type}&validity=${validity}&trip=${tripType}`);
  };

  return (
    <>
      <HeroSection onBook={() => navigate('/order')} />
      <FlightHotelPromo onBook={() => navigate('/order')} />
      <AirlineLogoScroller />
      <WhyChooseSection />
      <HowItWorksSection />
      <UseCasesSection />
      <DummyTicketExplainerSection />
      <LatestVisaGuidesSection />
      <PopularVisaPagesSection />
      <PricingSection onSelectPlan={handleSelectPlan} pricing={pricing} />
      <TemplatesSection />
      <FAQSection faqData={faqData} />
    </>
  );
};

// Main App
function App() {
  const [pricing, setPricing] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricingRes, faqRes] = await Promise.all([
          axios.get(`${API}/pricing`),
          axios.get(`${API}/faq`)
        ]);
        setPricing(pricingRes.data.pricing);
        setFaqData(faqRes.data.faq);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✈️</div>
          <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <CanonicalManager />
        <ScrollToTop />
        <GeoProvider>
          <LanguageProvider>
            <AuthProvider>
              <Header />
              <main>
                <Routes>
                <Route path="/" element={<HomePage pricing={pricing} faqData={faqData} />} />
                <Route path="/order" element={<Suspense fallback={<PageLoader />}><LazyBookingPage pricing={pricing} /></Suspense>} />
                <Route path="/book" element={<Suspense fallback={<PageLoader />}><LazyBookingPage pricing={pricing} /></Suspense>} />
                <Route path="/hotel" element={<Suspense fallback={<PageLoader />}><LazyBookingPage pricing={pricing} isHotelBooking={true} /></Suspense>} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/login" element={<Suspense fallback={<PageLoader />}><LazyAuthPage /></Suspense>} />
                <Route path="/orders" element={<Suspense fallback={<PageLoader />}><LazyOrdersPage /></Suspense>} />
                {/* Main admin route - consider using obscure URL in production */}
                <Route path="/admin" element={<Suspense fallback={<PageLoader />}><LazyAdminLoginPage /></Suspense>} />
                <Route path="/admin/dashboard" element={<Suspense fallback={<PageLoader />}><LazyAdminDashboard /></Suspense>} />
                {/* Obscured admin routes - use these for enhanced security */}
                <Route path="/mdt-secure-portal" element={<Suspense fallback={<PageLoader />}><LazyAdminLoginPage /></Suspense>} />
                <Route path="/mdt-secure-portal/dashboard" element={<Suspense fallback={<PageLoader />}><LazyAdminDashboard /></Suspense>} />
                <Route path="/feedback" element={<Suspense fallback={<PageLoader />}><LazyFeedbackPage /></Suspense>} />
                <Route path="/contact" element={<Suspense fallback={<PageLoader />}><LazyContactPage /></Suspense>} />
                <Route path="/faq" element={<Suspense fallback={<PageLoader />}><LazyFAQPage /></Suspense>} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><LazyPrivacyPage /></Suspense>} />
                <Route path="/terms" element={<Suspense fallback={<PageLoader />}><LazyTermsPage /></Suspense>} />
                <Route path="/blog" element={<Suspense fallback={<PageLoader />}><LazyBlogListPage /></Suspense>} />
                <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><LazyBlogPostPage /></Suspense>} />
                <Route path="/free-resources" element={<Suspense fallback={<PageLoader />}><LazyFreeResourcesPage /></Suspense>} />
                <Route path="/visa-guides" element={<Suspense fallback={<PageLoader />}><LazyVisaGuidesHub /></Suspense>} />
                {/* Track Order route removed per user request */}
                <Route path="/agent" element={<AgentSignupPage />} />
                <Route path="/affiliate" element={<AffiliateSignupPage />} />
                <Route path="/referral" element={<Suspense fallback={<PageLoader />}><LazyReferralPage /></Suspense>} />
                {/* Country-specific landing pages */}
                <Route path="/dummy-ticket-india" element={<Suspense fallback={<PageLoader />}><LazyIndiaPage /></Suspense>} />
                <Route path="/dummy-ticket-nigeria" element={<Suspense fallback={<PageLoader />}><LazyNigeriaPage /></Suspense>} />
                <Route path="/dummy-ticket-pakistan" element={<Suspense fallback={<PageLoader />}><LazyPakistanPage /></Suspense>} />
                <Route path="/onward-travel-thailand" element={<Suspense fallback={<PageLoader />}><LazyThailandPage /></Suspense>} />
                {/* Visa-specific landing pages */}
                <Route path="/schengen-visa-flight-reservation" element={<Suspense fallback={<PageLoader />}><LazySchengenVisaPage /></Suspense>} />
                <Route path="/uk-visa-flight-reservation" element={<Suspense fallback={<PageLoader />}><LazyUKVisaPage /></Suspense>} />
                <Route path="/thailand-onward-ticket" element={<Suspense fallback={<PageLoader />}><LazyThailandOnwardPage /></Suspense>} />
                <Route path="/canada-visa-flight-reservation" element={<Suspense fallback={<PageLoader />}><LazyCanadaVisaPage /></Suspense>} />
                <Route path="/dubai-visa-flight-reservation" element={<Suspense fallback={<PageLoader />}><LazyUAEVisaPage /></Suspense>} />
                <Route path="/us-visa-flight-reservation" element={<Suspense fallback={<PageLoader />}><LazyUSVisaPage /></Suspense>} />
                <Route path="/india-visa-flight-reservation" element={<Suspense fallback={<PageLoader />}><LazyIndiaVisaPage /></Suspense>} />
                {/* Schengen country service pages (high-intent SEO) */}
                <Route path="/flight-reservation-for-france-visa" element={<Suspense fallback={<PageLoader />}><LazyFranceVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-germany-visa" element={<Suspense fallback={<PageLoader />}><LazyGermanyVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-italy-visa" element={<Suspense fallback={<PageLoader />}><LazyItalyVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-spain-visa" element={<Suspense fallback={<PageLoader />}><LazySpainVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-netherlands-visa" element={<Suspense fallback={<PageLoader />}><LazyNetherlandsVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-belgium-visa" element={<Suspense fallback={<PageLoader />}><LazyBelgiumVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-switzerland-visa" element={<Suspense fallback={<PageLoader />}><LazySwitzerlandVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-austria-visa" element={<Suspense fallback={<PageLoader />}><LazyAustriaVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-greece-visa" element={<Suspense fallback={<PageLoader />}><LazyGreeceVisaPage /></Suspense>} />
                <Route path="/flight-reservation-for-portugal-visa" element={<Suspense fallback={<PageLoader />}><LazyPortugalVisaPage /></Suspense>} />
                {/* Brand keyword redirects */}
                <Route path="/dummyticket" element={<Navigate to="/" replace />} />
                <Route path="/my-dummy-ticket" element={<Navigate to="/" replace />} />
                <Route path="/dummy-ticket" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
            <Footer />
            <CookieConsentBanner />
          </AuthProvider>
        </LanguageProvider>
        </GeoProvider>
      </BrowserRouter>
    </div>
  );
}

const AgentSignupPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    enquiry_type: 'agent',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    message: ''
  });

  // SEO meta tags
  useEffect(() => {
    document.title = 'Become a B2B Travel Agent | Dummy Ticket Partner Program | MyDummyTicket';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Join MyDummyTicket B2B agent program. Earn 10-15% commission on dummy tickets for visa applications. White-label solutions for travel agencies. Sell flight reservations to Schengen, UK, US visa applicants.');
    }
    const setMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setMetaTag('keywords', 'B2B travel agent, dummy ticket reseller, flight reservation partner, visa documentation agent, travel agency partner program, white label dummy ticket, bulk dummy tickets, travel consultant program');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/b2b/enquiry`, formData);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      alert('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '120px', background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '500px', textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🤝</div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '12px' }}>Application Received!</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>Thank you for your interest in becoming a B2B Agent. Our team will review your application and contact you within 24-48 hours.</p>
          <a href="/" className="btn-primary">Back to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', paddingBottom: '60px', background: 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%)' }}>
      <div className="container" style={{ maxWidth: '700px' }}>
        {/* SEO Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '36px' }}>👔</div>
          <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px' }}>Become a B2B Travel Agent</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>
            Partner with MyDummyTicket and offer <strong>dummy tickets for visa applications</strong> to your clients
          </p>
        </div>

        {/* SEO Content Block */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>Why Partner With Us?</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
            As a B2B agent, you'll have access to our complete range of <strong>flight reservations for visa applications</strong>. 
            Help your clients with <strong>Schengen visa dummy tickets</strong>, <strong>UK visa flight itineraries</strong>, 
            <strong>US visa travel documentation</strong>, and <strong>proof of onward travel</strong> for digital nomads. 
            Our white-label solutions let you maintain your brand while offering competitive pricing.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🌍</div>
              <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>Global Coverage</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Dummy tickets for India, Nigeria, Pakistan, Philippines & more</div>
            </div>
            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>✈️</div>
              <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>All Visa Types</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Schengen, UK, US, Canada, UAE, Australia visas</div>
            </div>
            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>💼</div>
              <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>White Label</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Custom branding & API integration available</div>
            </div>
          </div>
        </div>

        <div className="form-card">
          <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '20px', marginBottom: '28px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#1e40af' }}>Agent Benefits:</h3>
            <ul style={{ fontSize: '13px', color: '#1e3a5f', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li><strong>10-15% Commission</strong> on all referred bookings</li>
              <li><strong>Dedicated Agent Code</strong> for tracking</li>
              <li><strong>White-label Options</strong> available</li>
              <li><strong>Priority Support</strong> for your clients</li>
              <li><strong>Monthly Payouts</strong> via bank transfer</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Company/Agency Name *</label>
                <input type="text" className="form-input" placeholder="Your travel agency name" value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} required data-testid="agent-company" />
              </div>
              <div>
                <label className="form-label">Contact Person *</label>
                <input type="text" className="form-input" placeholder="Full name" value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} required data-testid="agent-name" />
              </div>
              <div>
                <label className="form-label">Email Address *</label>
                <input type="email" className="form-input" placeholder="business@email.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required data-testid="agent-email" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Phone Number *</label>
                <input type="tel" className="form-input" placeholder="+44 123 456 7890" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required data-testid="agent-phone" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Tell us about your business</label>
                <textarea className="form-input" rows={4} placeholder="Describe your business, monthly volume, target market..." value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} style={{ resize: 'vertical' }} data-testid="agent-message" />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: '24px' }} disabled={loading} data-testid="agent-submit">
              {loading ? 'Submitting...' : 'Submit Agent Application'}
            </button>
          </form>
        </div>

        {/* Trust Badges */}
        <TrustBadgesInline />
      </div>
    </div>
  );
};

// Affiliate Signup Page
const AffiliateSignupPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    enquiry_type: 'affiliate',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    message: ''
  });

  // SEO meta tags
  useEffect(() => {
    document.title = 'Affiliate Program | Earn Money Promoting Dummy Tickets | MyDummyTicket';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Join MyDummyTicket affiliate program and earn 5-10% commission promoting dummy tickets for visa applications. Perfect for travel bloggers, visa consultants, and digital nomad influencers.');
    }
    const setMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setMetaTag('keywords', 'dummy ticket affiliate program, travel affiliate, visa consultant affiliate, earn money travel blog, flight reservation affiliate, digital nomad affiliate, Schengen visa affiliate, visa documentation referral');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/b2b/enquiry`, formData);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      alert('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '120px', background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '500px', textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🎉</div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '12px' }}>Application Received!</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>Thank you for joining our Affiliate Program! Our team will review your application and send you your affiliate link within 24 hours.</p>
          <a href="/" className="btn-primary">Back to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', paddingBottom: '60px', background: 'linear-gradient(180deg, #fdf2f8 0%, #ffffff 100%)' }}>
      <div className="container" style={{ maxWidth: '700px' }}>
        {/* SEO Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '36px' }}>💰</div>
          <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px' }}>Join Our Affiliate Program</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>
            Earn money promoting <strong>dummy tickets for visa applications</strong> to your audience
          </p>
        </div>

        {/* SEO Content Block */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>Perfect For Content Creators</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
            Are you a <strong>travel blogger</strong>, <strong>visa consultant</strong>, or <strong>digital nomad influencer</strong>? 
            Our affiliate program is perfect for anyone helping travelers with <strong>Schengen visa requirements</strong>, 
            <strong>proof of onward travel</strong>, or <strong>flight itineraries for visa applications</strong>. 
            Earn commissions every time someone books a dummy ticket through your unique link.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
            <div style={{ background: '#fdf2f8', padding: '14px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>📝</div>
              <div style={{ fontWeight: '600', fontSize: '12px' }}>Travel Bloggers</div>
            </div>
            <div style={{ background: '#fdf2f8', padding: '14px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>📺</div>
              <div style={{ fontWeight: '600', fontSize: '12px' }}>YouTubers</div>
            </div>
            <div style={{ background: '#fdf2f8', padding: '14px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>📱</div>
              <div style={{ fontWeight: '600', fontSize: '12px' }}>Influencers</div>
            </div>
            <div style={{ background: '#fdf2f8', padding: '14px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>🎓</div>
              <div style={{ fontWeight: '600', fontSize: '12px' }}>Visa Consultants</div>
            </div>
          </div>
        </div>

        <div className="form-card">
          <div style={{ background: '#fdf2f8', borderRadius: '12px', padding: '20px', marginBottom: '28px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#be185d' }}>Affiliate Benefits:</h3>
            <ul style={{ fontSize: '13px', color: '#831843', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li><strong>5-10% Commission</strong> on every sale</li>
              <li><strong>Unique Tracking Link</strong> for your content</li>
              <li><strong>Real-time Dashboard</strong> to track earnings</li>
              <li><strong>30-day Cookie</strong> duration</li>
              <li><strong>No Minimum Payout</strong> threshold</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label className="form-label">Your Name *</label>
                <input type="text" className="form-input" placeholder="Full name" value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} required data-testid="affiliate-name" />
              </div>
              <div>
                <label className="form-label">Email Address *</label>
                <input type="email" className="form-input" placeholder="your@email.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required data-testid="affiliate-email" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Website/Social Media URL</label>
                <input type="url" className="form-input" placeholder="https://yourwebsite.com or @yoursocial" value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} data-testid="affiliate-website" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Phone Number (Optional)</label>
                <input type="tel" className="form-input" placeholder="+1 234 567 8900" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} data-testid="affiliate-phone" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">How will you promote us?</label>
                <textarea className="form-input" rows={4} placeholder="Tell us about your audience and how you plan to promote MyDummyTicket..." value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} style={{ resize: 'vertical' }} data-testid="affiliate-message" />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: '24px', background: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)' }} disabled={loading} data-testid="affiliate-submit">
              {loading ? 'Submitting...' : 'Join Affiliate Program'}
            </button>
          </form>
        </div>

        {/* Trust Badges */}
        <TrustBadgesInline />
      </div>
    </div>
  );
};

// Contact Page
// FAQ Page (Standalone)
const FAQStandalonePage = () => {
  const { t } = useLanguage();
  const { format } = useGeo();
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useSEO(
    'Dummy Ticket FAQ | Flight Reservation Questions Answered | MyDummyTicket',
    'Frequently asked questions about dummy tickets, flight reservations for visa applications, proof of onward travel, PNR codes, and booking process. Learn how embassies verify flight bookings.',
    'dummy ticket FAQ, is dummy ticket legal, how embassies verify flight reservation, what is PNR number flight, proof of onward travel questions, dummy ticket delivery time, visa application help'
  );

  const faqCategories = [
    { id: 'all', label: '📋 All', count: 16 },
    { id: 'general', label: '❓ General', count: 5 },
    { id: 'booking', label: '✈️ Booking', count: 4 },
    { id: 'payment', label: '💳 Payment', count: 3 },
    { id: 'delivery', label: '📧 Delivery', count: 2 },
    { id: 'legal', label: '⚖️ Legal', count: 2 }
  ];

  const allFaqs = [
    // General - 5 FAQs
    { category: 'general', question: 'What is a dummy ticket or dummy flight reservation?', answer: 'A dummy ticket (also known as a flight reservation, flight itinerary, or onward ticket) is a confirmed flight booking with a valid PNR code that can be verified on airline websites. It\'s commonly used for visa applications, proof of onward travel, and immigration purposes. Our dummy tickets are real reservations made with actual airline partners. Unlike fake itineraries, our verifiable tickets can be checked on CheckMyTrip.com or airline websites, making them accepted by embassies worldwide.' },
    { category: 'general', question: 'Is a dummy ticket legal for visa applications?', answer: 'Yes, dummy tickets are completely legal. They are real flight reservations that simply have a shorter validity period. Many embassies and immigration officers accept them as proof of travel plans. Embassies understand that travelers don\'t want to risk buying expensive non-refundable tickets before visa approval. We recommend our Verifiable tickets for strict visa requirements (Schengen, UK, US, Canada, Australia) as they can be verified on airline websites.' },
    { category: 'general', question: 'Why do I need a dummy ticket for visa application?', answer: 'Many embassies and consulates require proof of travel plans (flight itinerary) before issuing a visa. However, buying a full-price ticket before getting your visa is risky - if your application is denied, you lose money on non-refundable tickets. A dummy ticket provides official proof of your travel plans at a fraction of the cost, giving you flexibility to book your actual flight once your visa is approved. It\'s the smart way to apply for visas.' },
    { category: 'general', question: 'What\'s the difference between Standard and Verifiable tickets?', answer: 'Standard tickets (£6.99 per adult) are instant auto-generated PDF itineraries with the airline logo - great for basic proof of travel. Verifiable tickets (£11.99 per adult) are actual airline reservations with real PNR codes. Our Bundle (£16.99 per adult) adds a real hotel confirmation booked by our team. Verifiable tickets are actual airline reservations with real PNR codes that can be checked on CheckMyTrip.com and airline websites. For Schengen visa, UK visa, US visa, Canada visa, we strongly recommend Verifiable tickets.' },
    { category: 'general', question: 'Which countries accept dummy tickets for visa?', answer: 'Dummy tickets are accepted for visa applications to virtually all countries including: Schengen Area (26 European countries), United Kingdom, United States, Canada, Australia, New Zealand, UAE, Singapore, Japan, South Korea, and many more. Our verifiable tickets with real PNR codes are specifically designed to meet the strictest embassy requirements. We\'ve helped over 50,000 travelers successfully obtain their visas.' },
    
    // Booking - 4 FAQs
    { category: 'booking', question: 'How long does it take to receive my dummy ticket?', answer: 'Standard tickets are delivered instantly via email within 1 minute of payment. Verifiable PNR tickets require manual processing and are delivered within 1 minute to 6 hours as we create real reservations through airline systems. You\'ll receive an email notification when your ticket is ready.' },
    { category: 'booking', question: 'Can I book dummy tickets for multiple passengers?', answer: 'Yes! You can add up to 9 passengers per booking - perfect for family visa applications or group travel. Each passenger appears on the same itinerary with their individual details (name, date of birth, nationality). Children (2–11) and infants (under 2) are automatically charged reduced rates. All passengers receive the same validity period and PNR code for verification.' },
    { category: 'booking', question: 'Can I book a round-trip dummy ticket?', answer: 'Absolutely! We offer both one-way and round-trip flight reservations. Round-trip bookings come with bundle savings of up to 31% off - great value for return journey requirements. Both outbound and return flights will appear on your itinerary with valid PNR codes (for Verifiable tickets). Many embassies prefer seeing round-trip itineraries as it demonstrates your intention to return home.' },
    { category: 'booking', question: 'Which airlines are included in verifiable tickets?', answer: 'Our Verifiable tickets use real airlines through our airline partners and GDS connections, including major carriers like: Lufthansa, British Airways, Emirates, Qatar Airways, Singapore Airlines, Air France, KLM, Turkish Airlines, Etihad, and 300+ other airlines worldwide. The specific airline depends on your route, selected dates, and availability. We always select reputable airlines that embassies recognize.' },
    
    // Payment - 3 FAQs
    { category: 'payment', question: 'What payment methods do you accept?', answer: 'We accept all major credit and debit cards including Visa, Mastercard, American Express, and Discover through our secure Stripe payment gateway. All transactions are protected with 256-bit SSL encryption and are PCI DSS compliant. We also support Apple Pay, Google Pay, and other digital wallets for convenient checkout. Payments are processed in USD, GBP, or EUR.' },
    { category: 'payment', question: 'Is my payment information secure?', answer: 'Yes, absolutely. We use Stripe for payment processing - the same payment provider used by Amazon, Google, and millions of businesses worldwide. Stripe is PCI DSS Level 1 certified (the highest level of payment security). Your card details are never stored on our servers and are encrypted end-to-end. We also use 3D Secure authentication for additional fraud protection.' },
    { category: 'payment', question: 'What is your refund policy?', answer: 'We offer full refunds within 24 hours of purchase if you haven\'t downloaded or used your ticket. For Verifiable tickets, refunds are processed within 5-7 business days as we need to cancel the actual airline reservation. After 24 hours, we offer partial refunds on a case-by-case basis. Please contact support@mydummyticket.com for any refund requests - our team responds within 2 hours.' },
    
    // Delivery - 2 FAQs
    { category: 'delivery', question: 'How will I receive my dummy flight ticket?', answer: 'Your dummy ticket will be delivered via email with PDF attachment - check spam folder if not in inbox. Standard tickets are instant, while Verifiable PNR tickets are processed manually and delivered within 1-6 hours.' },
    { category: 'delivery', question: 'Can I get my ticket resent or download again?', answer: 'Yes! You can access your ticket anytime using our Track Order feature. Simply enter your order number (e.g., ABC12345) or PNR code on our homepage. You can also contact our 24/7 support team via email (support@mydummyticket.com), and we\'ll resend your ticket immediately. We keep records of all bookings for 30 days for your convenience.' },
    
    // Legal - 2 FAQs
    { category: 'legal', question: 'Is my personal data safe with MyDummyTicket?', answer: 'Yes, we take data protection seriously. MyDummyTicket is registered with the UK Information Commissioner\'s Office (ICO) under registration number ZC006858 and is fully GDPR compliant. Your personal data is encrypted using AES-256 encryption, never shared with third parties (except airline systems for booking), and automatically deleted after 30 days. We follow strict data minimization principles - we only collect what\'s necessary for your booking.' },
    { category: 'legal', question: 'Is MyDummyTicket a legitimate UK company?', answer: 'Yes, MyDummyTicket is a registered UK brand. We operate from our UK office and are proud members of the Made in Britain campaign. Our payment processing is handled by Stripe (a publicly traded company), we\'re ICO registered for data protection (ZC006858), and we\'ve served over 50,000 customers since 2023.' }
  ];

  const filteredFaqs = activeCategory === 'all' ? allFaqs : allFaqs.filter(f => f.category === activeCategory);

  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '900px' }}>
        {/* SEO Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div className="eyebrow">Help Center</div>
          <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
            Dummy Ticket FAQ - Flight Reservation for Visa
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '700px', margin: '0 auto', lineHeight: '1.7' }}>
            Everything you need to know about <strong>dummy tickets</strong>, <strong>flight reservations for visa applications</strong>, 
            <strong>proof of onward travel</strong>, and <strong>verifiable PNR codes</strong>. 
            Trusted by 50,000+ travelers for Schengen, UK, US, Canada & Australia visas.
          </p>
        </div>

        {/* SEO Keywords Section */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '8px', 
          justifyContent: 'center', 
          marginBottom: '32px',
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '12px'
        }}>
          {['Dummy Ticket', 'Visa Application', 'Flight Reservation', 'PNR Code', 'Proof of Onward Travel', 'Schengen Visa', 'UK Visa', 'Flight Itinerary'].map(tag => (
            <span key={tag} style={{
              padding: '6px 12px',
              background: 'white',
              border: '1px solid var(--border-light)',
              borderRadius: '20px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              fontWeight: '500'
            }}>
              {tag}
            </span>
          ))}
        </div>

        {/* Category Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '32px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {faqCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                padding: '10px 18px',
                border: activeCategory === cat.id ? '2px solid var(--accent-teal)' : '2px solid var(--border-light)',
                borderRadius: '25px',
                background: activeCategory === cat.id ? 'var(--accent-teal)' : 'white',
                color: activeCategory === cat.id ? 'white' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredFaqs.map((faq, i) => (
            <div 
              key={i} 
              className="form-card"
              style={{ padding: 0, overflow: 'hidden' }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'left',
                  gap: '16px'
                }}
              >
                <span>{faq.question}</span>
                <span style={{ 
                  color: 'var(--accent-teal)', 
                  fontSize: '20px', 
                  fontWeight: '300',
                  flexShrink: 0,
                  transform: openIndex === i ? 'rotate(45deg)' : 'rotate(0)',
                  transition: 'transform 0.2s'
                }}>
                  +
                </span>
              </button>
              {openIndex === i && (
                <div style={{ 
                  padding: '0 24px 20px', 
                  color: 'var(--text-secondary)', 
                  fontSize: '14px', 
                  lineHeight: '1.8',
                  background: 'var(--bg-secondary)',
                  borderTop: '1px solid var(--border-light)'
                }}>
                  {localizePrices(faq.answer, format)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div style={{ 
          marginTop: '48px', 
          padding: '32px', 
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
          borderRadius: '16px',
          textAlign: 'center',
          color: 'white'
        }}>
          <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>Still have questions?</h3>
          <p style={{ opacity: 0.9, marginBottom: '24px', fontSize: '16px' }}>Our support team is available 24/7 to help you.</p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="/contact" 
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px', 
                background: 'white', 
                color: '#0f766e',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '15px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              📧 Contact Us
            </a>
          </div>
        </div>

        {/* Internal Links Section for SEO */}
        <div style={{ 
          marginTop: '48px', 
          padding: '32px', 
          background: 'var(--bg-secondary)', 
          borderRadius: '16px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: 'var(--text-primary)' }}>📚 Related Resources</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <Link to="/order" style={{ 
              padding: '16px', 
              background: 'white', 
              borderRadius: '12px', 
              textDecoration: 'none',
              border: '1px solid var(--border-light)',
              display: 'block'
            }}>
              <div style={{ fontWeight: '600', color: 'var(--accent-teal)', marginBottom: '4px' }}>🎫 Book Your Flight Reservation</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Get your verifiable dummy ticket in 1 minute</div>
            </Link>
            <Link to="/templates" style={{ 
              padding: '16px', 
              background: 'white', 
              borderRadius: '12px', 
              textDecoration: 'none',
              border: '1px solid var(--border-light)',
              display: 'block'
            }}>
              <div style={{ fontWeight: '600', color: 'var(--accent-teal)', marginBottom: '4px' }}>📄 Free Schengen Templates</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Download cover letter and itinerary templates</div>
            </Link>
            <Link to="/blog/schengen-visa-flight-reservation" style={{ 
              padding: '16px', 
              background: 'white', 
              borderRadius: '12px', 
              textDecoration: 'none',
              border: '1px solid var(--border-light)',
              display: 'block'
            }}>
              <div style={{ fontWeight: '600', color: 'var(--accent-teal)', marginBottom: '4px' }}>🇪🇺 Schengen Visa Guide</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Complete guide to flight reservations for Schengen</div>
            </Link>
            <Link to="/blog/uk-visa-dummy-ticket" style={{ 
              padding: '16px', 
              background: 'white', 
              borderRadius: '12px', 
              textDecoration: 'none',
              border: '1px solid var(--border-light)',
              display: 'block'
            }}>
              <div style={{ fontWeight: '600', color: 'var(--accent-teal)', marginBottom: '4px' }}>🇬🇧 UK Visa Guide</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>UK visa dummy ticket requirements explained</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Free Templates Page
const TemplatesPage = () => {
  const { t } = useLanguage();
  const { format } = useGeo();
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templates = [
    {
      id: 1,
      name: 'Standard One-Way',
      type: 'standard',
      tripType: 'one_way',
      desc: 'Professional PDF flight itinerary for basic visa applications.',
      features: ['Clean GDS format', 'Passenger details', 'Flight information', 'Instant delivery'],
      image: '📄',
      pdfUrl: '/samples/standard_oneway.pdf',
      price: '£6.99'
    },
    {
      id: 2,
      name: 'Standard Round-Trip',
      type: 'standard',
      tripType: 'round_trip',
      desc: 'Complete round-trip flight itinerary with outbound and return flights.',
      features: ['Both flights included', 'Bundle discount', 'Return flight details', 'Single document'],
      image: '⇄',
      pdfUrl: '/samples/standard_roundtrip.pdf',
      price: '£6.99'
    },
    {
      id: 3,
      name: 'Verifiable One-Way',
      type: 'verifiable',
      tripType: 'one_way',
      desc: 'Real PNR code verifiable on CheckMyTrip and airline websites.',
      features: ['Real PNR code', 'Verifiable online', 'Airline confirmation', 'Embassy accepted'],
      image: '🛡️',
      pdfUrl: '/samples/verifiable_oneway.pdf',
      price: '£11.99'
    },
    {
      id: 4,
      name: 'Verifiable Round-Trip',
      type: 'verifiable',
      tripType: 'round_trip',
      desc: 'Complete verifiable round-trip reservation with real PNR codes.',
      features: ['Real PNR code', 'Both flights verifiable', 'Bundle savings', 'Best for Schengen'],
      image: '🛡️',
      pdfUrl: '/samples/verifiable_roundtrip.pdf',
      price: '£14.99'
    },
    {
      id: 5,
      name: 'Verifiable + Hotel Bundle',
      type: 'bundle',
      tripType: 'one_way',
      desc: 'Complete visa package: Verifiable flight ticket + Booking.com style hotel confirmation.',
      features: ['Real PNR code', 'Hotel confirmation', 'Embassy accepted', 'Best for Schengen visa'],
      image: '✈️🏨',
      pdfUrl: '/api/samples/verifiable-hotel-bundle',
      price: '£16.99',
      badge: 'MOST POPULAR'
    },
    {
      id: 6,
      name: 'Standard + Hotel Bundle',
      type: 'bundle',
      tripType: 'one_way',
      desc: 'Flight itinerary + Booking.com style hotel confirmation for basic visa applications.',
      features: ['Flight itinerary', 'Hotel confirmation', 'Instant delivery', 'Budget friendly'],
      image: '📄🏨',
      pdfUrl: '/api/samples/standard-hotel-bundle',
      price: '£16.99',
      badge: 'BUNDLE'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '1100px' }}>
        {/* SEO Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div className="eyebrow">Free Samples</div>
          <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
            Dummy Ticket Templates
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '700px', margin: '0 auto', lineHeight: '1.7' }}>
            View our professional dummy flight ticket templates. Our flight reservations look exactly like real airline confirmations and are accepted by embassies worldwide for visa applications, proof of onward travel, and immigration requirements.
          </p>
        </div>

        {/* SEO Keywords Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)', 
          padding: '24px 32px', 
          borderRadius: '16px', 
          marginBottom: '40px',
          border: '1px solid #99f6e4'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f766e' }}>
            What Our Dummy Tickets Include
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {[
              'Valid PNR Code', 'Flight Itinerary', 'Visa Application Ready', 'Proof of Onward Travel',
              'Immigration Compliant', 'Airline Confirmation', 'Booking Reference', 'Instant Delivery',
              'Schengen Visa', 'UK Visa', 'US Visa', 'Tourist Visa'
            ].map(tag => (
              <span key={tag} style={{
                padding: '6px 14px',
                background: 'white',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#0f766e'
              }}>
                ✓ {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Template Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '48px' }}>
          {templates.map(tpl => (
            <div 
              key={tpl.id} 
              className="form-card" 
              style={{ 
                padding: '28px',
                border: selectedTemplate === tpl.id ? '2px solid var(--accent-teal)' : '1px solid var(--border-light)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setSelectedTemplate(selectedTemplate === tpl.id ? null : tpl.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ fontSize: '48px' }}>{tpl.image}</div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ 
                    padding: '4px 12px', 
                    background: tpl.type === 'verifiable' ? '#dcfce7' : '#f1f5f9',
                    color: tpl.type === 'verifiable' ? '#166534' : '#475569',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    {tpl.type}
                  </span>
                  <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--accent-teal)' }}>
                    {localizePrices(tpl.price, format)}
                  </span>
                </div>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>{tpl.name}</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px', lineHeight: '1.6' }}>{tpl.desc}</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                {tpl.features.map((feat, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <span style={{ color: 'var(--accent-teal)' }}>✓</span>
                    {feat}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <a 
                  href={tpl.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary" 
                  style={{ width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', textDecoration: 'none' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  📥 View Sample PDF
                </a>
                <a 
                  href={`/order?documentType=${tpl.type}`}
                  className="btn-primary" 
                  style={{ width: '100%', textAlign: 'center', textDecoration: 'none' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Get This Template →
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '800', textAlign: 'center', marginBottom: '32px' }}>
            How to Get Your Dummy Ticket
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {[
              { step: '01', icon: '📝', title: 'Fill Details', desc: 'Enter your route, dates, and passenger information as it appears on your passport.' },
              { step: '02', icon: '💳', title: 'Pay Securely', desc: 'Complete payment via Stripe. 256-bit encryption protects your data.' },
              { step: '03', icon: '✈️', title: 'Get Ticket', desc: 'Receive your dummy ticket via email within 1 minute.' }
            ].map((s, i) => (
              <div key={i} className="form-card" style={{ textAlign: 'center', padding: '32px' }}>
                <div style={{ fontSize: '12px', fontWeight: '800', color: 'var(--accent-teal)', marginBottom: '12px' }}>{s.step}</div>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>{s.icon}</div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>{s.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SEO Content Block */}
        <div className="form-card" style={{ padding: '32px', marginBottom: '48px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '20px' }}>
            Why Choose My Dummy Ticket for Your Visa Application?
          </h2>
          <div style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: '1.8' }}>
            <p style={{ marginBottom: '16px' }}>
              <strong>My Dummy Ticket</strong> is the UK's leading provider of <strong>dummy flight tickets</strong> and <strong>flight reservations for visa applications</strong>. 
              Our professional <strong>dummy tickets</strong> are accepted by embassies worldwide, including <strong>Schengen visa</strong>, <strong>UK visa</strong>, and <strong>US visa</strong> applications.
            </p>
            <p style={{ marginBottom: '16px' }}>
              Need <strong>proof of onward travel</strong>? Our verifiable flight reservations come with real <strong>PNR codes</strong> that can be checked on airline websites. 
              We work with over 300 airlines through our airline partners to provide authentic <strong>flight itineraries</strong> for your immigration requirements.
            </p>
            <p>
              Whether you need a <strong>dummy ticket for visa</strong>, <strong>flight reservation for embassy</strong>, or <strong>proof of return flight</strong>, 
              we deliver within 1 minute via email. Trusted by 50,000+ travelers worldwide.
            </p>
          </div>
        </div>

        {/* Trust Badges - Matching Contact Page Design */}
        <div className="form-card" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: 'white', marginBottom: '48px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Why Trust Us?</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src="https://customer-assets.emergentagent.com/job_flightproof-2/artifacts/aduv5nxr_Information_Commissioners_Office_logo.388db5f2.webp" alt="ICO" style={{ height: '30px' }} />
              <span style={{ opacity: 0.9 }}>ICO Registered: ZC006858</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src="https://customer-assets.emergentagent.com/job_flightproof-2/artifacts/wgz88jdu_made-in-britain-logo-white.519a2cb0.webp" alt="Made in Britain" style={{ height: '35px' }} />
              <span style={{ opacity: 0.9 }}>Proudly British Company</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🔒</span>
              <span style={{ opacity: 0.9 }}>256-bit SSL Encryption</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={{ 
          textAlign: 'center', 
          padding: '48px', 
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
          borderRadius: '20px',
          color: 'white'
        }}>
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '16px' }}>
            Ready to Get Your Dummy Ticket?
          </h2>
          <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
            Join 50,000+ travelers who trust My Dummy Ticket for their visa applications. Instant delivery guaranteed.
          </p>
          <Link to="/order" className="btn-primary" style={{ 
            background: 'white', 
            color: 'var(--accent-teal)', 
            padding: '16px 40px', 
            fontSize: '16px',
            textDecoration: 'none',
            display: 'inline-block'
          }}>
            Book Your Flight Reservation — Starting from {format(6.99)}
          </Link>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPage = () => {
  const { t } = useLanguage();
  
  useSEO(
    'Privacy Policy | Data Protection | MyDummyTicket',
    'MyDummyTicket privacy policy. Learn how we collect, use, and protect your personal data. ICO registered (ZC006858), GDPR compliant. Your data is safe with us.'
  );
  
  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '900px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
            Privacy Policy
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', marginBottom: '24px' }}>
            Your privacy matters to us. This policy explains how we collect, use, and protect your data.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
            <span style={{ 
              padding: '8px 16px', 
              background: '#dcfce7', 
              borderRadius: '20px', 
              fontSize: '13px', 
              fontWeight: '600',
              color: '#166534'
            }}>
              ✓ GDPR Compliant
            </span>
            <span style={{ 
              padding: '8px 16px', 
              background: '#dbeafe', 
              borderRadius: '20px', 
              fontSize: '13px', 
              fontWeight: '600',
              color: '#1e40af'
            }}>
              ✓ UK Data Protection
            </span>
          </div>
        </div>

        {/* Privacy Content */}
        <div className="form-card" style={{ padding: '40px', marginBottom: '32px' }}>
          {/* Section 1 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              1. Information We Collect
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.7' }}>
              We collect information you provide directly when using our service:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '16px' }}>
              <li><strong>Personal details:</strong> Full name, email address, phone number</li>
              <li><strong>Travel information:</strong> Passport details, travel dates, destinations</li>
              <li><strong>Payment information:</strong> Processed securely through Stripe/PayPal (we do not store card details)</li>
              <li><strong>Communication data:</strong> Support inquiries and correspondence</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: '1.7' }}>
              <strong>Automatically collected data:</strong>
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Device information and browser type</li>
              <li>IP address and approximate location</li>
              <li>Usage data and interaction with our website</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              2. How We Use Your Information
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              We use your information to:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Process and deliver your flight reservation documents</li>
              <li>Send order confirmations via email</li>
              <li>Provide customer support</li>
              <li>Process payments securely</li>
              <li>Improve our services and website</li>
              <li>Comply with legal obligations</li>
              <li>Prevent fraud and ensure security</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              3. Information Sharing
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              We do <strong>NOT</strong> sell your personal information. We only share data with:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Payment processors (Stripe, PayPal) for transaction processing</li>
              <li>Email service providers for order delivery</li>
              <li>Legal authorities when required by law</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '12px', lineHeight: '1.7' }}>
              All third-party services are bound by data protection agreements.
            </p>
          </div>

          {/* Section 4 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              4. Data Retention
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              We retain your information for:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li><strong>Order records:</strong> 7 years (for legal and accounting purposes)</li>
              <li><strong>Customer support communications:</strong> 2 years</li>
              <li><strong>Website analytics:</strong> 26 months</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '12px', lineHeight: '1.7' }}>
              You can request deletion of your personal data at any time (subject to legal retention requirements).
            </p>
          </div>

          {/* Section 5 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              5. Your Rights (GDPR)
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              Under UK GDPR and data protection laws, you have the right to:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '12px', lineHeight: '1.7' }}>
              To exercise these rights, contact us at <a href="mailto:privacy@mydummyticket.com" style={{ color: 'var(--accent-teal)' }}>privacy@mydummyticket.com</a>
            </p>
          </div>

          {/* Section 6 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              6. Cookies & Tracking
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              We use cookies and similar technologies for:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li><strong>Essential cookies:</strong> Required for website functionality</li>
              <li><strong>Analytics cookies:</strong> To understand how visitors use our site</li>
              <li><strong>Marketing cookies:</strong> To show relevant advertisements (with consent)</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '12px', lineHeight: '1.7' }}>
              You can manage cookie preferences through our cookie banner or browser settings.
            </p>
          </div>

          {/* Section 7 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              7. Data Security
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              We implement industry-standard security measures:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>SSL/TLS encryption for all data transmission</li>
              <li>Secure payment processing via PCI-DSS compliant providers</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and employee training</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '12px', lineHeight: '1.7' }}>
              No system is 100% secure. We promptly notify affected users of any data breaches.
            </p>
          </div>

          {/* Section 8 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              8. International Transfers
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              Your data may be processed outside the UK/EEA. We ensure appropriate safeguards:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Standard Contractual Clauses with service providers</li>
              <li>Adequacy decisions where applicable</li>
              <li>Compliance with UK GDPR requirements</li>
            </ul>
          </div>

          {/* Section 9 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              9. Children's Privacy
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
              Our service is not intended for children under 16. We do not knowingly collect data from children. 
              If you believe we have collected data from a child, please contact us immediately.
            </p>
          </div>

          {/* Section 10 - Contact */}
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              10. Contact Us
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.7' }}>
              For privacy-related inquiries:
            </p>
            <div style={{ background: 'var(--bg-secondary)', padding: '20px', borderRadius: '12px' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                <strong>Email:</strong> <a href="mailto:privacy@mydummyticket.com" style={{ color: 'var(--accent-teal)' }}>privacy@mydummyticket.com</a>
              </p>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                <strong>Data Controller:</strong> MyDummyTicket.com is owned and operated by Qwizt It Solution Ltd
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '8px' }}>
                Company Number: 15243972
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                Registered in England & Wales, United Kingdom
              </p>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginTop: '16px', fontSize: '14px', lineHeight: '1.7' }}>
              You have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data has been mishandled.
            </p>
          </div>
        </div>

        {/* Trust Badges */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '48px', 
          padding: '32px',
          background: 'var(--bg-secondary)',
          borderRadius: '16px',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>🔒</span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>SSL Secured</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>✓</span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>GDPR Compliant</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>🛡️</span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>PCI-DSS</span>
          </div>
        </div>

        {/* Last Updated */}
        <div style={{ textAlign: 'center', marginBottom: '48px', color: 'var(--text-muted)', fontSize: '14px' }}>
          Last updated: February 2025
        </div>
      </div>
    </div>
  );
};

// Terms & Conditions Page
const TermsPage = () => {
  const { t } = useLanguage();
  
  useSEO(
    'Terms & Conditions | Service Agreement | MyDummyTicket',
    'Terms and conditions for using MyDummyTicket dummy flight reservation service. Read our service agreement, refund policy, and usage guidelines.'
  );
  
  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '900px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
            Terms & Conditions
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
            Please read these terms carefully before using our service.
          </p>
        </div>

        {/* Important Notice Banner */}
        <div style={{ 
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
          padding: '24px 32px', 
          borderRadius: '16px', 
          marginBottom: '32px',
          border: '1px solid #fcd34d'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#92400e' }}>
            ⚠️ Important Notice
          </h2>
          <p style={{ color: '#78350f', lineHeight: '1.7' }}>
            MyDummyTicket.com is a digital document service. We do <strong>NOT</strong> sell airline tickets. 
            Our documents are for visa application and travel planning purposes only. 
            They do not guarantee visa approval or right of entry.
          </p>
        </div>

        {/* Terms Content */}
        <div className="form-card" style={{ padding: '40px', marginBottom: '32px' }}>
          {/* Section 1 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              1. About Our Service
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              MyDummyTicket.com is a digital document service that provides flight reservation confirmations for visa applications and travel planning purposes.
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>We are <strong>NOT</strong> an airline, travel agency, or ticket seller</li>
              <li>We do not sell actual airline tickets or operate any flights</li>
              <li>Our service creates temporary flight reservations (also known as 'dummy tickets' or 'flight itineraries') that include real PNR codes verifiable on airline systems</li>
              <li>Documents provided are for visa application, proof of onward travel, and travel planning purposes only</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              2. Nature of Documents
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Our documents contain real PNR codes created through airline reservation systems</li>
              <li>These are <strong>TEMPORARY</strong> reservations that are held without full payment and will automatically expire</li>
              <li>Documents do <strong>NOT</strong> guarantee: visa approval, entry to any country, right to travel, or actual flight booking</li>
              <li>It is the customer's responsibility to ensure the document meets their specific visa or travel requirements</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              3. Auto-Expiry Policy
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>All reservations have a validity period selected at time of purchase (24 hours or 48 hours)</li>
              <li>When the validity period expires, the reservation is automatically cancelled from airline systems</li>
              <li>Download links for documents will be disabled upon expiry</li>
              <li>Customers should ensure the validity period covers their visa application processing time</li>
              <li>We are not responsible for visa delays that extend beyond the reservation validity period</li>
            </ul>
          </div>

          {/* Section 4 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              4. Pricing & Payment
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>All prices are displayed in US Dollars (USD) and include applicable fees</li>
              <li>Payment is processed securely through Stripe or PayPal</li>
              <li>Once payment is confirmed, your order is processed immediately</li>
              <li>Prices may vary based on document type (Standard vs Verifiable) and validity period</li>
            </ul>
          </div>

          {/* Section 5 - Refund Policy */}
          <div id="refund" style={{ marginBottom: '32px', scrollMarginTop: '120px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              5. Refund Policy
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              Refunds are <strong>ONLY</strong> provided in the following circumstances:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '16px' }}>
              <li><strong>Technical failure:</strong> If we fail to deliver the document due to a technical error on our end</li>
              <li><strong>Non-delivery:</strong> If the document is not delivered within 24 hours of payment confirmation</li>
              <li><strong>Duplicate charge:</strong> If you are charged multiple times for the same order</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              Refunds are <strong>NOT</strong> provided for:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Change of mind after document delivery</li>
              <li>Visa rejection or denial</li>
              <li>Immigration or border entry issues</li>
              <li>Incorrect information provided by the customer</li>
              <li>Expiry of reservation validity period</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '16px', lineHeight: '1.7' }}>
              To request a refund, contact us within 7 days of purchase with your order reference number.
            </p>
          </div>

          {/* Section 6 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              6. Customer Responsibilities
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Provide accurate and complete information (names, dates, passport details as they appear on travel documents)</li>
              <li>Verify all details before submitting an order</li>
              <li>Ensure the selected validity period is sufficient for your needs</li>
              <li>Download and save your documents before the expiry period</li>
              <li>Check specific requirements with the relevant embassy, consulate, or immigration authority</li>
            </ul>
          </div>

          {/* Section 7 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              7. Data Protection & Privacy
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>We collect personal information solely to process your order and deliver documents</li>
              <li>Your data is stored securely and not shared with third parties except as necessary to provide our service</li>
              <li>We may retain order records for accounting and legal compliance purposes</li>
              <li>You may request deletion of your personal data by contacting our support team</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '12px', lineHeight: '1.7' }}>
              For more details, please see our <a href="/privacy" style={{ color: 'var(--accent-teal)' }}>Privacy Policy</a>.
            </p>
          </div>

          {/* Section 8 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              8. Limitation of Liability
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.7' }}>
              MyDummyTicket.com shall not be liable for:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Visa rejection or denial</li>
              <li>Immigration detention or deportation</li>
              <li>Flight delays, cancellations, or changes by airlines</li>
              <li>Any indirect, incidental, or consequential damages</li>
              <li>Losses arising from customer-provided incorrect information</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', marginTop: '12px', lineHeight: '1.7' }}>
              Our maximum liability is limited to the amount paid for the specific order in question.
            </p>
          </div>

          {/* Section 9 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              9. Intellectual Property
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>All content, designs, and materials on MyDummyTicket.com are owned by us or our licensors</li>
              <li>You may not copy, reproduce, or redistribute our content without written permission</li>
              <li>The documents provided are for your personal use only and may not be resold or redistributed</li>
            </ul>
          </div>

          {/* Section 10 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              10. Governing Law
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>These terms and conditions are governed by the laws of England and Wales</li>
              <li>Any disputes arising from the use of our service shall be subject to the exclusive jurisdiction of the courts of England and Wales</li>
              <li>If any provision of these terms is found to be invalid, the remaining provisions shall continue to apply</li>
            </ul>
          </div>

          {/* Section 11 */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              11. Changes to Terms
            </h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>We reserve the right to update these terms and conditions at any time</li>
              <li>Continued use of our service after changes constitutes acceptance of the new terms</li>
              <li>The date of last update will be displayed at the bottom of this page</li>
            </ul>
          </div>

          {/* Section 12 - Contact */}
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--accent-teal)' }}>
              12. Contact Us
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.7' }}>
              For any questions about these terms or our service:
            </p>
            <div style={{ background: 'var(--bg-secondary)', padding: '20px', borderRadius: '12px' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                <strong>Email:</strong> <a href="mailto:support@mydummyticket.com" style={{ color: 'var(--accent-teal)' }}>support@mydummyticket.com</a>
              </p>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                <strong>MyDummyTicket.com is owned and operated by:</strong>
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>
                <strong>Qwizt It Solution Ltd</strong>
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '4px' }}>
                Company Number: 15243972
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                Registered in England & Wales, United Kingdom
              </p>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '48px', 
          padding: '32px',
          background: 'var(--bg-secondary)',
          borderRadius: '16px',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>🔒</span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>SSL Secured</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>🇬🇧</span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>UK Registered</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>💳</span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Stripe Payments</span>
          </div>
        </div>

        {/* Last Updated */}
        <div style={{ textAlign: 'center', marginBottom: '48px', color: 'var(--text-muted)', fontSize: '14px' }}>
          Last updated: February 2025
        </div>
      </div>
    </div>
  );
};

// Blog Data with SEO Content
const BLOG_POSTS = [
  {
    id: 1,
    slug: 'what-is-dummy-ticket-visa-application',
    title: 'What is a Dummy Ticket? Complete Guide to Flight Reservations for Visa Applications',
    excerpt: 'Learn everything about dummy tickets, flight reservations, and how they help with visa applications. Understand PNR codes, validity periods, and embassy requirements.',
    category: 'Visa Guide',
    author: 'Sarah Johnson',
    date: 'February 10, 2025',
    readTime: '12 min read',
    image: 'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/937dcf45c73543e4ef8b391b21587046a688ecc709f1bab5460ae69cda0c159d.png',
    images: [
      'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/b6600ca4f96e6f4ba9748b2eaa92293e72ed49a514dfe59366255d4d2f881f72.png',
      'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/937dcf45c73543e4ef8b391b21587046a688ecc709f1bab5460ae69cda0c159d.png'
    ],
    tags: ['dummy ticket', 'visa application', 'flight reservation', 'PNR code', 'travel documents'],
    content: `
## Introduction to Dummy Tickets

A **dummy ticket**, also known as a **flight reservation** or **flight itinerary**, is a confirmed airline booking that serves as proof of travel plans without requiring full payment for the actual flight. This document has become an essential tool for travelers worldwide, particularly those applying for visas or needing to demonstrate **proof of onward travel** at immigration checkpoints.

When you're planning international travel, one of the most common requirements you'll encounter is the need to show a confirmed flight booking. However, purchasing a full-price airline ticket before your visa is approved is risky and financially impractical. This is where **dummy tickets for visa applications** become invaluable.

### Why Do You Need a Dummy Ticket?

The primary reasons travelers seek dummy tickets include:

1. **Visa Applications**: Most embassies and consulates require proof of travel plans when processing visa applications. A **dummy flight ticket** provides this proof without the financial risk of buying an actual ticket.

2. **Proof of Onward Travel**: Immigration officers at borders often ask travelers to prove they have plans to leave the country. A **flight reservation for visa** purposes satisfies this requirement.

3. **Schengen Visa Requirements**: The **Schengen visa application** process specifically asks for confirmed flight bookings as part of the documentation.

4. **UK and US Visa Applications**: Both **UK visa** and **US visa** applications typically require evidence of travel itinerary.

## How Does a Dummy Ticket Work?

A dummy ticket is essentially a real airline reservation that has been placed on temporary hold. When you order a **dummy ticket** from a reputable provider like My Dummy Ticket, the system creates an actual reservation through airline systems with our airline partners.

### The PNR Code Explained

Every **dummy ticket** comes with a **PNR code** (Passenger Name Record), which is a unique alphanumeric identifier assigned by the airline's reservation system. This code allows anyone to verify the booking's authenticity by checking it on the airline's official website or on third-party verification sites like CheckMyTrip.

The **verifiable PNR** feature is what distinguishes a high-quality dummy ticket from a simple PDF document. When embassy officials or immigration officers want to confirm your travel plans, they can enter your PNR code and see that your reservation exists in the airline's system.

## Types of Dummy Tickets Available

### Standard Dummy Tickets

Standard dummy tickets are professional PDF documents that look identical to real airline confirmations. They include:

- Passenger name and details
- Flight information (route, date, time)
- Booking reference number
- Airline branding

While these documents appear authentic, they cannot be verified online. Standard tickets are suitable for situations where verification is unlikely or not required.

### Verifiable Dummy Tickets

**Verifiable dummy tickets** are actual reservations created in airline systems with real **PNR codes**. These offer the highest level of authenticity and are recommended for:

- **Schengen visa applications**
- **UK visa applications**
- **US visa applications**
- Immigration checkpoints where officers may verify your booking

At My Dummy Ticket, we specialize in verifiable reservations that can withstand scrutiny from even the most thorough immigration officials.

## Validity Periods: Choosing the Right Duration

When ordering a **dummy ticket for visa**, you'll need to select a validity period. This determines how long your reservation remains active in the airline system before it automatically expires.

### 24-Hour Validity
Best for last-minute visa appointments or quick immigration crossings. The **24-hour dummy ticket** is the most affordable option.

### 48-Hour Validity
Ideal for standard visa interview schedules. Gives you a buffer period for any delays in your appointment.

### 7-Day Validity
Recommended for **Schengen visa** applications and other complex visa processes that may take several days to complete.

### 14-Day Validity
The longest available option, perfect for situations where you need extended proof of travel plans or dealing with slow embassy processing times.

## Using Dummy Tickets for Different Visa Types

### Schengen Visa Application

The **Schengen visa** covers 27 European countries and is one of the most popular visa types worldwide. The application process specifically requires:

- Confirmed flight reservation showing entry and exit from the Schengen area
- Round-trip itinerary preferred
- Dates matching your stated travel plans

A **dummy ticket for Schengen visa** should show your complete travel itinerary, including any internal flights within Europe if applicable.

### UK Visa Application

The **UK visa application** process, whether for tourism, business, or study, requires evidence of your travel arrangements. British embassies typically want to see:

- Entry flight to the UK
- Return flight to your home country
- Dates aligning with your visa application

### US Visa Application

**US visa** applications, particularly B1/B2 tourist visas, often require proof of ties to your home country, including evidence that you plan to return. A **return flight reservation** demonstrates your intention to leave the United States.

## The Legal Aspect: Are Dummy Tickets Legal?

One of the most common questions we receive is: "**Are dummy tickets legal?**" The answer is unequivocally yes.

**Dummy tickets are 100% legal** and widely accepted. They are genuine airline reservations, simply held without full payment. This is a standard practice in the travel industry, and embassies worldwide recognize and accept them.

What makes dummy tickets legitimate:

1. They are real reservations in airline systems
2. They contain valid PNR codes
3. They represent genuine booking intents
4. They do not involve any falsification of documents

However, it's important to use reputable providers who create actual reservations rather than fabricating documents, as the latter could constitute fraud.

## How to Order Your Dummy Ticket

Getting your **flight reservation for visa application** is simple with My Dummy Ticket:

1. **Select Your Route**: Enter your departure and arrival airports
2. **Choose Dates**: Pick your travel dates
3. **Select Ticket Type**: Standard or Verifiable
4. **Choose Validity**: 24 hours or 48 hours
5. **Add Passengers**: Include details for all travelers
6. **Complete Payment**: Secure checkout via Stripe
7. **Receive Instantly**: Get your ticket via email

## Conclusion

A **dummy ticket** is an essential tool for modern travelers navigating the complex world of visa applications and immigration requirements. Whether you need a **flight reservation for Schengen visa**, **proof of onward travel**, or documentation for any other purpose, dummy tickets provide a safe, legal, and affordable solution.

At My Dummy Ticket, we've helped over 50,000 travelers secure their visas with professional **dummy flight tickets** and **verifiable PNR codes**. Our instant delivery system ensures you get your documents exactly when you need them.

Ready to secure your visa? [Book your dummy ticket now](/book) and join thousands of satisfied travelers who chose the smart way to prove their travel plans.
    `
  },
  {
    id: 2,
    slug: 'schengen-visa-flight-reservation-guide',
    title: 'Schengen Visa Flight Reservation: Everything You Need to Know in 2025',
    excerpt: 'Complete guide to Schengen visa flight reservations. Learn about embassy requirements, document preparation, and how to get the perfect flight itinerary for your European trip.',
    category: 'Schengen Visa',
    author: 'Michael Chen',
    date: 'February 8, 2025',
    readTime: '15 min read',
    image: 'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/e0a29b98c849f51f3869e6ffc8644f50edc412c51e1fe0af9906b8c9b9d56491.png',
    images: [
      'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/e0a29b98c849f51f3869e6ffc8644f50edc412c51e1fe0af9906b8c9b9d56491.png',
      'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/3260096a21807a37c4e8a1b804deba96490ca844798fac4e6c928caf8b5e814f.png'
    ],
    tags: ['Schengen visa', 'Europe travel', 'flight reservation', 'visa requirements', 'travel itinerary'],
    content: `
## Introduction to Schengen Visa Requirements

The **Schengen visa** is your gateway to exploring 27 European countries with a single visa. From the romantic streets of Paris to the historic landmarks of Rome, from the beautiful beaches of Greece to the stunning fjords of Norway, the Schengen area offers endless possibilities for travelers.

However, obtaining a **Schengen visa** requires careful preparation, and one of the most critical documents you'll need is a **flight reservation for Schengen visa** application. In this comprehensive guide, we'll walk you through everything you need to know about **Schengen visa flight reservations** in 2025.

### What is the Schengen Area?

The Schengen Area comprises 27 European countries that have abolished passport control at their mutual borders. This means once you have a valid **Schengen visa**, you can travel freely between member states without additional border checks.

**Schengen member countries include:**
- France, Germany, Italy, Spain, Portugal
- Netherlands, Belgium, Luxembourg, Austria, Switzerland
- Greece, Poland, Czech Republic, Hungary, Slovakia
- Denmark, Sweden, Norway, Finland, Iceland
- Estonia, Latvia, Lithuania, Slovenia, Croatia
- Malta, Liechtenstein

## Why Embassy Requires Flight Reservation

When you apply for a **Schengen visa**, the embassy needs to verify several aspects of your trip:

1. **Entry Point**: Which Schengen country will you enter first?
2. **Duration of Stay**: How long do you plan to stay?
3. **Exit Plan**: When and how will you leave the Schengen area?
4. **Travel Dates**: Do your flights align with your stated travel plans?

A **flight reservation for visa** application addresses all these questions. It shows the embassy that you have concrete travel plans and intend to comply with visa regulations.

### The Risk of Buying Actual Tickets

Many first-time applicants make the mistake of purchasing actual airline tickets before visa approval. This approach carries significant risks:

- **Visa Denial**: If your visa is denied, you may lose hundreds or thousands of dollars on non-refundable tickets
- **Date Changes**: The embassy might approve your visa for different dates than requested
- **Processing Delays**: Visa processing can take weeks, during which flight prices may change

This is why experienced travelers use **dummy tickets** or **flight reservations** instead of actual bookings.

## Types of Flight Documents for Schengen Visa

### Option 1: Standard Flight Itinerary

A standard **flight itinerary** is a document that looks like an airline confirmation but isn't linked to an actual reservation. While some embassies accept these, they're becoming less reliable as verification systems improve.

### Option 2: Verifiable Flight Reservation

A **verifiable flight reservation** is an actual booking in the airline's system with a real **PNR code**. This is the gold standard for **Schengen visa applications** because:

- Embassy staff can verify the booking online
- It demonstrates genuine travel intent
- It matches what airlines would issue for a confirmed booking

At My Dummy Ticket, all our **Schengen visa flight reservations** come with verifiable PNR codes that can be checked on airline websites and CheckMyTrip.

## Creating the Perfect Schengen Flight Itinerary

### Entry Requirements

Your **flight reservation** should show:

1. **Departure from your home country**: The flight should originate from where you legally reside
2. **First Schengen entry point**: This determines which embassy processes your application
3. **Clear routing**: If you have layovers, ensure they're logical and don't raise questions

### Return Flight Considerations

The **return flight** is crucial for proving you intend to leave the Schengen area. Your reservation should show:

- Return to your home country (not a third country)
- Date within the 90-day Schengen limit
- Reasonable travel duration matching your application

### Round-Trip vs. One-Way Reservations

For **Schengen visa applications**, **round-trip reservations** are almost always required. A one-way ticket may raise red flags about your intention to return home.

**Pro tip**: My Dummy Ticket offers a **20% discount on round-trip bookings**, making it more affordable to get the documentation you need.

## Country-Specific Requirements

While the Schengen visa application process is standardized, individual embassies may have specific preferences:

### German Embassy
- Prefers Lufthansa or partner airline reservations
- Appreciates detailed itineraries
- May call to verify large group bookings

### French Embassy
- Accepts reservations from any airline
- Likes to see complete travel plans
- Values clearly organized documents

### Italian Embassy
- Particularly thorough in document review
- May require additional proof of accommodation
- Appreciates cover letters explaining travel plans

### Spanish Embassy
- Generally straightforward requirements
- Accepts standard flight reservations
- Processes applications relatively quickly

## Step-by-Step: Getting Your Schengen Flight Reservation

Follow these steps to obtain your **flight reservation for Schengen visa**:

### Step 1: Determine Your First Entry Point

Identify which Schengen country you'll enter first. This embassy will process your application. If visiting multiple countries for equal time, apply to the country of first entry.

### Step 2: Plan Your Dates

Calculate your intended:
- Departure date from home
- Arrival in Schengen area
- Return date (within 90 days)

### Step 3: Choose Your Provider

Select a reputable **dummy ticket** provider like My Dummy Ticket that offers:
- Verifiable PNR codes
- Instant delivery
- Multiple validity options
- Professional formatting

### Step 4: Complete Your Booking

Visit [My Dummy Ticket booking page](/book) and enter:
- Your name (exactly as it appears on your passport)
- Route details
- Travel dates
- Passenger information

### Step 5: Select Validity Period

For **Schengen visa applications**, we recommend:
- **24-hour validity**: For airport/immigration checks
- **48-hour validity**: Most popular for visa applications

### Step 6: Receive Your Documents

After payment, you'll receive your **flight reservation** via:
- Email (with PDF attachment)

## Common Mistakes to Avoid

### Mistake 1: Mismatched Names
Ensure your name on the **flight reservation** matches your passport exactly. Any discrepancy can cause visa delays or denial.

### Mistake 2: Unrealistic Itineraries
Don't create impossible travel schedules. If your itinerary shows visiting 10 countries in 5 days, embassy staff will question your application.

### Mistake 3: Expired Reservations
Submit your visa application while your **flight reservation** is still valid. Expired reservations are worthless.

### Mistake 4: Wrong Entry Point
Apply at the embassy of your first Schengen entry point. Applying to the wrong embassy can result in rejection.

## Frequently Asked Questions

**Q: Can the embassy tell it's a dummy ticket?**
A: Professional **verifiable reservations** are real bookings in airline systems. They appear identical to any other reservation.

**Q: What if my visa is approved for different dates?**
A: You can book actual flights for the approved dates. The dummy ticket served its purpose in the application process.

**Q: How far in advance should I get my reservation?**
A: Order your **flight reservation** 1-2 days before your visa appointment to ensure it's still valid.

**Q: Do I need reservations for internal Schengen flights?**
A: Generally no, but if your itinerary shows significant internal travel, having these reservations can strengthen your application.

## Conclusion

A proper **flight reservation for Schengen visa** application is essential for visa success. By understanding embassy requirements and using verifiable reservations, you significantly increase your chances of approval.

My Dummy Ticket has helped thousands of travelers obtain their **Schengen visas** with professional **flight reservations** featuring real PNR codes. Our instant delivery ensures you're always prepared for your visa appointment.

Ready to start your European adventure? [Get your Schengen flight reservation now](/book) and take the first step toward exploring Europe's most beautiful destinations.
    `
  },
  {
    id: 3,
    slug: 'proof-of-onward-travel-immigration',
    title: 'Proof of Onward Travel: How to Satisfy Immigration Requirements Without Buying a Ticket',
    excerpt: 'Discover how to provide proof of onward travel for immigration without purchasing expensive flights. Learn about legal alternatives and tips for smooth border crossings.',
    category: 'Immigration',
    author: 'Emma Williams',
    date: 'February 5, 2025',
    readTime: '10 min read',
    image: 'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/3260096a21807a37c4e8a1b804deba96490ca844798fac4e6c928caf8b5e814f.png',
    images: [
      'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/3260096a21807a37c4e8a1b804deba96490ca844798fac4e6c928caf8b5e814f.png',
      'https://static.prod-images.emergentagent.com/jobs/6a5caea2-53c2-4c73-9006-e93b09eb60fa/images/b6600ca4f96e6f4ba9748b2eaa92293e72ed49a514dfe59366255d4d2f881f72.png'
    ],
    tags: ['proof of onward travel', 'immigration', 'border crossing', 'onward ticket', 'travel requirements'],
    content: `
## Understanding Proof of Onward Travel

**Proof of onward travel** is one of the most misunderstood requirements in international travel. Whether you're a digital nomad, backpacker, or business traveler, understanding this requirement can save you from unexpected problems at borders and airports.

In this comprehensive guide, we'll explain everything you need to know about **onward travel requirements**, why they exist, and how to satisfy them without breaking the bank.

### What is Proof of Onward Travel?

**Proof of onward travel** is documentation showing that you have plans to leave a country within your permitted stay period. This typically comes in the form of:

- Flight tickets or reservations
- Bus or train tickets
- Cruise bookings
- Any transportation booking that shows you're leaving

Immigration authorities use this requirement to ensure travelers don't overstay their visas or enter with the intention of remaining illegally.

## Why Do Countries Require Onward Travel Proof?

### Immigration Control

The primary purpose of **onward travel requirements** is immigration control. Countries want assurance that visitors will leave when their visa or visa-free period expires.

### Airline Liability

Airlines can face significant fines if they transport passengers who are denied entry. To protect themselves, many airlines check for **proof of onward travel** at check-in.

### Common Countries Requiring Onward Travel Proof

**Strict enforcement:**
- United States
- United Kingdom
- Australia
- New Zealand
- Philippines
- Indonesia (Bali)
- Thailand
- Peru
- Brazil
- Costa Rica

**Occasional enforcement:**
- Most Schengen countries
- Japan
- South Korea
- Malaysia
- Mexico

## The Problem with Buying Actual Tickets

### Financial Risk

Purchasing a real flight ticket just to show at immigration is financially risky because:

- Tickets are often non-refundable or have high change fees
- Your plans may change after entry
- Flight prices fluctuate significantly
- You might find better deals later

### Flexibility Issues

As a traveler, especially if you're:
- Backpacking through multiple countries
- Working remotely and unsure of exact dates
- Waiting for visa approvals for next destination
- Traveling spontaneously

You need flexibility that expensive airline tickets don't provide.

## Legal Solutions for Proof of Onward Travel

### Solution 1: Dummy Tickets / Flight Reservations

The most popular and practical solution is using a **dummy ticket** or **flight reservation**. These are actual airline bookings held without full payment.

**Benefits:**
- Verifiable PNR code
- Looks identical to a real booking
- Much cheaper than actual tickets
- Available in multiple validity periods
- Instant delivery

At My Dummy Ticket, we provide **proof of onward travel** documentation that's accepted worldwide. Our reservations come with real PNR codes verifiable on airline websites.

### Solution 2: Refundable Tickets

Some travelers buy fully refundable tickets and cancel after clearing immigration. However, this approach has drawbacks:

- Ties up significant funds
- Requires credit card with high limit
- Refund processing can take weeks
- Some airlines make cancellation difficult

### Solution 3: Bus or Train Tickets

For land borders, bus or train tickets can serve as **proof of onward travel**. However:

- Not accepted for flights
- Limited routes available
- May not satisfy all immigration officers

## How to Use Dummy Tickets for Onward Travel Proof

### At Airport Check-In

When checking in for your flight, the airline staff may ask for your **onward travel** documentation. Simply show them your **dummy ticket** confirmation email or PDF.

### At Immigration

Immigration officers may request **proof of leaving** the country. Present your **flight reservation** showing your exit plans.

### Tips for Smooth Processing

1. **Print your documents**: Have physical copies, not just digital
2. **Match dates**: Ensure your onward flight is within your visa period
3. **Be confident**: Present your documents matter-of-factly
4. **Have backup**: Save documents on your phone and email

## Country-Specific Requirements

### United States

The US is particularly strict about **onward travel**. For visa-free entry under the Visa Waiver Program (ESTA), you must have:

- Return or onward ticket
- Proof of sufficient funds
- Ties to home country

### United Kingdom

UK immigration wants to see:
- Return flight booking
- Accommodation details
- Proof of funds
- Purpose of visit

### Australia

Australia requires:
- Confirmed outbound flight
- Valid visa (unless eligible for ETA)
- Sufficient funds

### Thailand

Thai immigration may ask for:
- Outbound ticket
- Proof of $500-$1000 funds
- Hotel booking

### Philippines

The Philippines strictly enforces **onward travel** requirements:
- Return or onward ticket mandatory
- Airlines will deny boarding without it
- Immigration may refuse entry

## Frequently Asked Questions

**Q: Is using a dummy ticket legal?**
A: Yes, absolutely. A **dummy ticket** is a real airline reservation, just not fully paid. It's a legitimate service used by millions of travelers.

**Q: What if immigration calls the airline to verify?**
A: Our **verifiable flight reservations** exist in airline systems. They will show as confirmed bookings when checked.

**Q: Can I use the same dummy ticket for multiple countries?**
A: Yes, as long as it shows you leaving the region within required timeframes.

**Q: What validity period should I choose?**
A: For **proof of onward travel**:
- 24 hours: Same-day border crossing
- 48 hours: Most common choice
- 48 hours: Extended verification for visa applications

**Q: What if I don't end up using the flight shown?**
A: That's fine. The **dummy ticket** served its purpose. You can book different flights later.

## Best Practices for International Travel

### Before Your Trip

1. Research destination's **onward travel** requirements
2. Check if your airline enforces the requirement
3. Order your **proof of onward travel** 1-2 days before departure
4. Print multiple copies

### At the Airport

1. Have documents easily accessible
2. Be prepared to show **onward travel proof**
3. Stay calm and confident
4. Know your rights

### At Immigration

1. Answer questions honestly
2. Show documents when requested
3. Have accommodation details ready
4. Know your planned travel dates

## Conclusion

**Proof of onward travel** is a reality of international travel that every traveler must navigate. Rather than risking expensive tickets or travel disruptions, smart travelers use **dummy tickets** and **flight reservations** to satisfy these requirements legally and affordably.

My Dummy Ticket has helped over 50,000 travelers provide **proof of onward travel** for immigration checkpoints worldwide. Our instant delivery and verifiable PNR codes ensure you're always prepared.

Don't let **onward travel requirements** stress you out. [Get your flight reservation now](/book) and travel with confidence knowing you have all the documentation you need.

**Traveling soon?** Our [booking page](/book) offers instant **dummy tickets** with real PNR codes. Join thousands of satisfied travelers who chose the smart way to satisfy immigration requirements.
    `
  }
];

// Blog List Page
const BlogListPage = () => {
  const { t } = useLanguage();
  const { format } = useGeo();
  const [posts, setPosts] = useState(BLOG_POSTS);
  const [loading, setLoading] = useState(true);
  
  useSEO(
    'Dummy Ticket Blog | Visa Guides, Proof of Onward Travel Tips | MyDummyTicket',
    'Expert guides on dummy tickets for Schengen, UK, US & Canada visas. Learn about flight reservations, proof of onward travel requirements, PNR verification and visa application tips.',
    'dummy ticket for schengen visa, proof of onward travel, flight reservation for visa, dummy ticket for indian passport, visa application guide, PNR verification, dummy ticket tips'
  );
  
  // Fetch posts from API and merge with hardcoded posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        const apiPosts = (res.data.posts || []).map(p => ({
          ...p,
          date: p.created_at ? new Date(p.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Recent',
          readTime: p.read_time || '5 min read'
        }));
        // Combine API posts (first) with hardcoded posts, avoiding duplicates by slug
        const hardcodedSlugs = new Set(BLOG_POSTS.map(p => p.slug));
        const uniqueApiPosts = apiPosts.filter(p => !hardcodedSlugs.has(p.slug));
        setPosts([...uniqueApiPosts, ...BLOG_POSTS]);
      } catch (err) {
        console.error('Failed to fetch blog posts:', err);
        setPosts(BLOG_POSTS);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);
  
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Loading blog posts...</p>
      </div>
    );
  }
  
  const featuredPost = posts.find(p => p.is_featured) || posts[0];
  const otherPosts = posts.filter(p => p.id !== featuredPost.id);
  
  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '1200px' }}>
        {/* SEO Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div className="eyebrow">Travel Blog</div>
          <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
            Dummy Ticket & Visa Application Guides
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '700px', margin: '0 auto', lineHeight: '1.7' }}>
            Expert guides on dummy tickets, flight reservations, visa applications, and proof of onward travel. 
            Learn everything you need to know about travel documentation from our experienced team.
          </p>
        </div>

        {/* Main Content with Sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '40px' }}>
          {/* Left: Blog Posts */}
          <div>
            {/* Featured Post */}
            <div style={{ marginBottom: '32px' }}>
              <Link to={'/blog/' + featuredPost.slug} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="form-card" style={{ padding: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  <div style={{ padding: '40px' }}>
                    <span style={{ 
                      display: 'inline-block',
                      padding: '6px 12px', 
                      background: '#dcfce7', 
                      color: '#166534', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      fontWeight: '600',
                      marginBottom: '16px'
                    }}>
                      ⭐ Featured
                    </span>
                    <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '16px', lineHeight: '1.3' }}>
                      {featuredPost.title}
                    </h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.7', marginBottom: '20px' }}>
                      {featuredPost.excerpt}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      <span>{featuredPost.author}</span>
                      <span>•</span>
                      <span>{featuredPost.date}</span>
                      <span>•</span>
                      <span>{featuredPost.readTime}</span>
                    </div>
                  </div>
                  <div style={{ 
                    backgroundImage: 'url(' + featuredPost.image + ')', 
                    backgroundSize: 'cover', 
                    backgroundPosition: 'center',
                    minHeight: '280px'
                  }} />
                </div>
              </Link>
            </div>

            {/* Blog Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '32px' }}>
              {otherPosts.map(post => (
                <Link key={post.id} to={'/blog/' + post.slug} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="form-card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
                    <div style={{ 
                      backgroundImage: 'url(' + post.image + ')', 
                      backgroundSize: 'cover', 
                      backgroundPosition: 'center',
                      height: '160px'
                    }} />
                    <div style={{ padding: '20px' }}>
                      <span style={{ 
                        display: 'inline-block',
                        padding: '4px 10px', 
                        background: 'var(--bg-secondary)', 
                        color: 'var(--accent-teal)', 
                        borderRadius: '20px', 
                        fontSize: '11px', 
                        fontWeight: '600',
                        marginBottom: '10px'
                      }}>
                        {post.category}
                      </span>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '10px', lineHeight: '1.4' }}>
                        {post.title}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>
                        <span>{post.date}</span>
                        <span>•</span>
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Right: Sidebar */}
          <aside style={{ position: 'sticky', top: '100px', height: 'fit-content' }}>
            {/* Recent Articles */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📰 Recent Articles
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {posts.slice(0, 4).map(article => (
                  <Link key={article.id} to={'/blog/' + article.slug} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div style={{ display: 'flex', gap: '12px', padding: '12px', borderRadius: '10px', transition: 'background 0.2s' }} 
                         onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                         onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        borderRadius: '8px',
                        backgroundImage: `url(${article.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        flexShrink: 0
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '6px', lineHeight: '1.4', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {article.title}
                        </h4>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick Book CTA */}
            <div style={{ background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', borderRadius: '16px', padding: '24px', color: 'white', marginBottom: '24px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>✈️ Need a Dummy Ticket?</h4>
              <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '16px', lineHeight: '1.5' }}>
                Get your verifiable flight reservation in minutes. Perfect for visa applications.
              </p>
              <Link to="/book" style={{ 
                display: 'block', 
                textAlign: 'center', 
                padding: '12px', 
                background: 'white', 
                borderRadius: '8px', 
                color: 'var(--accent-teal)', 
                textDecoration: 'none', 
                fontSize: '14px', 
                fontWeight: '600' 
              }}>
                Book Now - From {format(6.99)}
              </Link>
            </div>

            {/* Popular Topics */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px' }}>🏷️ Popular Topics</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {['Schengen Visa', 'UK Visa', 'Dummy Ticket', 'Flight Reservation', 'Onward Travel'].map(tag => (
                  <Link 
                    key={tag} 
                    to="/book"
                    style={{
                      padding: '6px 12px',
                      background: '#f1f5f9',
                      borderRadius: '20px',
                      fontSize: '12px',
                      color: '#475569',
                      textDecoration: 'none',
                      fontWeight: '500'
                    }}
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* SEO Keywords Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)', 
          padding: '32px', 
          borderRadius: '16px', 
          marginBottom: '48px',
          marginTop: '32px',
          border: '1px solid #99f6e4'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#0f766e' }}>
            All Topics
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {[
              'dummy ticket', 'flight reservation', 'visa application', 'Schengen visa', 
              'proof of onward travel', 'PNR code', 'flight itinerary', 'UK visa',
              'US visa', 'verifiable PNR', 'travel documents', 'onward ticket',
              'dummy ticket for visa', 'flight reservation for visa'
            ].map(tag => (
              <Link 
                key={tag} 
                to="/book"
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  borderRadius: '20px',
                  fontSize: '13px',
                  color: '#0f766e',
                  textDecoration: 'none',
                  fontWeight: '500',
                  border: '1px solid #99f6e4'
                }}
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div style={{ 
          textAlign: 'center', 
          padding: '48px', 
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
          borderRadius: '20px',
          color: 'white',
          marginBottom: '48px'
        }}>
          <h2 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '16px' }}>
            Need a Dummy Ticket for Your Visa Application?
          </h2>
          <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
            Get your verifiable flight reservation in under 1 minute. Trusted by 50,000+ travelers worldwide.
          </p>
          <Link to="/book" className="btn-primary" style={{ 
            background: 'white', 
            color: 'var(--accent-teal)', 
            padding: '16px 40px', 
            fontSize: '16px',
            textDecoration: 'none',
            display: 'inline-block'
          }}>
            Book Your Dummy Ticket Now →
          </Link>
        </div>
      </div>
    </div>
  );
};

// Blog Post Page
const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  // Fetch post from API or fall back to hardcoded
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        // First try API
        const res = await axios.get(`${API}/blog/${slug}`);
        if (res.data.post) {
          const apiPost = {
            ...res.data.post,
            date: res.data.post.created_at ? new Date(res.data.post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Recent',
            readTime: res.data.post.read_time || '5 min read'
          };
          setPost(apiPost);
          setRelatedPosts(BLOG_POSTS.slice(0, 2));
        }
      } catch (err) {
        // Fall back to hardcoded posts
        const hardcodedPost = BLOG_POSTS.find(p => p.slug === slug);
        if (hardcodedPost) {
          setPost(hardcodedPost);
          setRelatedPosts(BLOG_POSTS.filter(p => p.id !== hardcodedPost.id).slice(0, 2));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Set SEO meta tags for blog post
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (post) {
      // Set title
      document.title = `${post.title} | MyDummyTicket Blog`;
      
      // Set meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.meta_description || post.excerpt);
      }
      
      // Set or create Open Graph tags
      const setMetaTag = (property, content, isOG = false) => {
        const selector = isOG ? `meta[property="${property}"]` : `meta[name="${property}"]`;
        let meta = document.querySelector(selector);
        if (!meta) {
          meta = document.createElement('meta');
          if (isOG) {
            meta.setAttribute('property', property);
          } else {
            meta.setAttribute('name', property);
          }
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };
      
      // Open Graph tags for social sharing
      setMetaTag('og:title', post.meta_title || post.title, true);
      setMetaTag('og:description', post.meta_description || post.excerpt, true);
      setMetaTag('og:image', post.image, true);
      setMetaTag('og:type', 'article', true);
      setMetaTag('og:url', window.location.href, true);
      
      // Twitter Card tags
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', post.meta_title || post.title);
      setMetaTag('twitter:description', post.meta_description || post.excerpt);
      setMetaTag('twitter:image', post.image);
      
      // Article meta tags
      setMetaTag('article:author', post.author);
      setMetaTag('article:published_time', post.date);
      
      // Keywords from meta_keywords or tags
      const keywords = post.meta_keywords || (post.tags && post.tags.length > 0 ? post.tags.join(', ') : '');
      if (keywords) {
        setMetaTag('keywords', keywords);
      }
    }
  }, [post]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '120px', textAlign: 'center' }}>
        <p>Loading post...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '120px', textAlign: 'center' }}>
        <h1>Post not found</h1>
        <Link to="/blog">Back to Blog</Link>
      </div>
    );
  }

  // Simple markdown-like rendering with link conversion
  const renderContent = (content) => {
    const sections = content.split('\n\n');
    return sections.map((section, i) => {
      const trimmed = section.trim();
      
      // Headers
      if (trimmed.startsWith('## ')) {
        return <h2 key={i} style={{ fontSize: '24px', fontWeight: '700', marginTop: '40px', marginBottom: '16px', color: 'var(--text-primary)' }}>{trimmed.replace('## ', '')}</h2>;
      }
      if (trimmed.startsWith('### ')) {
        return <h3 key={i} style={{ fontSize: '20px', fontWeight: '700', marginTop: '32px', marginBottom: '12px', color: 'var(--text-primary)' }}>{trimmed.replace('### ', '')}</h3>;
      }
      
      // Lists
      if (trimmed.startsWith('- ') || trimmed.startsWith('1. ')) {
        const items = trimmed.split('\n').filter(item => item.trim());
        const isOrdered = trimmed.startsWith('1.');
        const ListTag = isOrdered ? 'ol' : 'ul';
        return (
          <ListTag key={i} style={{ marginBottom: '16px', paddingLeft: '24px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            {items.map((item, j) => {
              const text = item.replace(/^[-\d.]\s*/, '');
              return <li key={j} style={{ marginBottom: '8px' }}>{renderTextWithLinks(text)}</li>;
            })}
          </ListTag>
        );
      }
      
      // Blockquote/bold sections
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        return <p key={i} style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '16px' }}>{trimmed.replace(/\*\*/g, '')}</p>;
      }
      
      // Regular paragraphs
      if (trimmed) {
        return <p key={i} style={{ marginBottom: '16px', color: 'var(--text-secondary)', lineHeight: '1.8', fontSize: '16px' }}>{renderTextWithLinks(trimmed)}</p>;
      }
      
      return null;
    });
  };

  // Convert markdown links and bold text
  const renderTextWithLinks = (text) => {
    // First handle links [text](/url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(renderBoldText(text.slice(lastIndex, match.index), lastIndex));
      }
      // Add the link
      parts.push(
        <Link 
          key={'link-' + match.index} 
          to={match[2]} 
          style={{ color: 'var(--accent-teal)', fontWeight: '600', textDecoration: 'underline' }}
        >
          {match[1]}
        </Link>
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(renderBoldText(text.slice(lastIndex), lastIndex));
    }
    
    return parts.length > 0 ? parts : renderBoldText(text, 0);
  };

  // Handle bold text
  const renderBoldText = (text, keyPrefix) => {
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={keyPrefix + '-' + lastIndex}>{text.slice(lastIndex, match.index)}</span>);
      }
      parts.push(<strong key={keyPrefix + '-bold-' + match.index} style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(<span key={keyPrefix + '-end'}>{text.slice(lastIndex)}</span>);
    }
    
    return parts.length > 0 ? parts : text;
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        {/* Back Link */}
        <Link to="/blog" style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: '8px', 
          color: 'var(--accent-teal)', 
          textDecoration: 'none',
          marginBottom: '24px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          ← Back to Blog
        </Link>

        {/* Article Header */}
        <article>
          <header style={{ marginBottom: '32px' }}>
            <span style={{ 
              display: 'inline-block',
              padding: '6px 14px', 
              background: 'var(--bg-secondary)', 
              color: 'var(--accent-teal)', 
              borderRadius: '20px', 
              fontSize: '12px', 
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              {post.category}
            </span>
            <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '20px', lineHeight: '1.2' }}>
              {post.title}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
              <span>By {post.author}</span>
              <span>•</span>
              <span>{post.date}</span>
              <span>•</span>
              <span>{post.readTime}</span>
            </div>
          </header>

          {/* Featured Image */}
          <div style={{ 
            borderRadius: '16px', 
            overflow: 'hidden', 
            marginBottom: '32px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            aspectRatio: '16/9'
          }}>
            <img 
              src={post.image} 
              alt={post.title} 
              style={{ width: '100%', height: '400px', objectFit: 'cover' }}
              width="800"
              height="400"
              loading="eager"
            />
          </div>

          {/* Article Content */}
          <div className="form-card" style={{ padding: '40px', marginBottom: '32px' }}>
            {renderContent(post.content)}
            
            {/* In-article images */}
            {post.images && post.images.length > 1 && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '16px', 
                margin: '32px 0',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                {post.images.slice(0, 2).map((img, i) => (
                  <img 
                    key={i}
                    src={img} 
                    alt={post.title + ' - Image ' + (i + 1)} 
                    style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                    width="400"
                    height="200"
                    loading="lazy"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-muted)' }}>Tags:</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {post.tags.map(tag => (
                <Link 
                  key={tag}
                  to="/book"
                  style={{
                    padding: '6px 14px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '20px',
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none'
                  }}
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA Box */}
          <div style={{ 
            background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
            padding: '32px',
            borderRadius: '16px',
            textAlign: 'center',
            color: 'white',
            marginBottom: '32px'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
              Ready to Get Your Dummy Ticket?
            </h3>
            <p style={{ opacity: 0.9, marginBottom: '20px' }}>
              Join 50,000+ travelers who trust My Dummy Ticket for their visa applications.
            </p>
            <Link to="/order" className="btn-primary" style={{ 
              background: 'white', 
              color: 'var(--accent-teal)',
              textDecoration: 'none',
              display: 'inline-block'
            }}>
              Book Your Flight Reservation →
            </Link>
          </div>

          {/* Internal Links Section for SEO */}
          <div style={{ 
            padding: '24px', 
            background: 'var(--bg-secondary)', 
            borderRadius: '12px',
            marginBottom: '32px'
          }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>📌 Useful Resources</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <Link to="/order" style={{ 
                padding: '10px 16px', 
                background: 'var(--accent-teal)', 
                color: 'white', 
                borderRadius: '8px', 
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                🎫 Book Your Flight Reservation
              </Link>
              <Link to="/templates" style={{ 
                padding: '10px 16px', 
                background: 'white', 
                color: 'var(--accent-teal)', 
                borderRadius: '8px', 
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: '600',
                border: '1px solid var(--accent-teal)'
              }}>
                📄 Free Schengen Templates
              </Link>
              <Link to="/faq" style={{ 
                padding: '10px 16px', 
                background: 'white', 
                color: 'var(--text-secondary)', 
                borderRadius: '8px', 
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: '500',
                border: '1px solid var(--border-light)'
              }}>
                ❓ FAQ
              </Link>
            </div>
          </div>

          {/* Related Posts */}
          <div style={{ marginBottom: '48px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>Related Articles</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {relatedPosts.map(related => (
                <Link key={related.id} to={'/blog/' + related.slug} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="form-card" style={{ padding: '20px', display: 'flex', gap: '16px' }}>
                    <div style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '8px',
                      backgroundImage: 'url(' + related.image + ')',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      flexShrink: 0
                    }} />
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', lineHeight: '1.4' }}>
                        {related.title.slice(0, 60)}...
                      </h4>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{related.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

// Free Resources Page - Visa Application Templates
const FreeResourcesPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  useSEO(
    'Free Visa Templates | Schengen Cover Letter, Flight Itinerary Samples | MyDummyTicket',
    'Download free Schengen visa cover letter templates, flight itinerary samples, and visa application checklists. Professional templates for UK, US, Canada visa applications.',
    'schengen visa cover letter sample, schengen visa cover letter template, free flight itinerary template, visa application cover letter, flight reservation template download, schengen visa flight reservation template'
  );
  
  const resources = [
    {
      id: 'cover-letter-schengen',
      title: 'Schengen Visa Cover Letter Template',
      description: 'Professional cover letter template for Schengen visa applications. Includes sections for travel purpose, itinerary, and financial proof.',
      format: 'DOCX',
      size: '24 KB',
      category: 'Cover Letters',
      icon: '📝'
    },
    {
      id: 'cover-letter-uk',
      title: 'UK Visa Cover Letter Template',
      description: 'Comprehensive cover letter for UK Standard Visitor visa. Tailored for tourism, business, and family visits.',
      format: 'DOCX',
      size: '22 KB',
      category: 'Cover Letters',
      icon: '📝'
    },
    {
      id: 'cover-letter-us',
      title: 'US B1/B2 Visa Cover Letter Template',
      description: 'Professional template for US tourist and business visa applications. Includes DS-160 reference guidance.',
      format: 'DOCX',
      size: '26 KB',
      category: 'Cover Letters',
      icon: '📝'
    },
    {
      id: 'travel-itinerary',
      title: 'Travel Itinerary Template',
      description: 'Day-by-day travel itinerary template with accommodation details, activities, and transportation schedule.',
      format: 'XLSX',
      size: '18 KB',
      category: 'Itineraries',
      icon: '📅'
    },
    {
      id: 'accommodation-letter',
      title: 'Host Accommodation Letter Template',
      description: 'Letter template for hosts providing accommodation. Includes sponsor details and proof of residence section.',
      format: 'DOCX',
      size: '20 KB',
      category: 'Accommodation',
      icon: '🏠'
    },
    {
      id: 'financial-statement',
      title: 'Financial Statement Template',
      description: 'Template for presenting bank statements and financial documents in a visa-friendly format.',
      format: 'XLSX',
      size: '15 KB',
      category: 'Financial',
      icon: '💰'
    },
    {
      id: 'employment-letter',
      title: 'Employment Letter Template',
      description: 'Letter from employer confirming employment status, salary, and approved leave for travel.',
      format: 'DOCX',
      size: '19 KB',
      category: 'Employment',
      icon: '💼'
    },
    {
      id: 'noc-letter',
      title: 'No Objection Certificate (NOC) Template',
      description: 'NOC template for employees traveling abroad. Confirms employer permission and return guarantee.',
      format: 'DOCX',
      size: '17 KB',
      category: 'Employment',
      icon: '📋'
    },
    {
      id: 'travel-insurance-checklist',
      title: 'Travel Insurance Checklist',
      description: 'Comprehensive checklist for choosing travel insurance that meets visa requirements.',
      format: 'PDF',
      size: '12 KB',
      category: 'Checklists',
      icon: '✅'
    },
    {
      id: 'visa-checklist-schengen',
      title: 'Schengen Visa Document Checklist',
      description: 'Complete checklist of all required documents for Schengen visa application.',
      format: 'PDF',
      size: '14 KB',
      category: 'Checklists',
      icon: '✅'
    }
  ];

  const downloadResource = (resourceId) => {
    // Trigger download from backend
    window.open(`${API_URL}/api/resources/download/${resourceId}`, '_blank');
  };

  const categories = [...new Set(resources.map(r => r.category))];

  return (
    <div style={{ minHeight: '100vh', paddingTop: '100px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '1100px' }}>
        {/* SEO-Optimised Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div className="eyebrow">Free Downloads</div>
          <h1 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
            Free Visa Application Resources
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '700px', margin: '0 auto', lineHeight: '1.7' }}>
            Download professional templates to strengthen your visa application. Our free resources include cover letters, 
            itinerary planners, and document checklists used by thousands of successful applicants worldwide.
          </p>
        </div>

        {/* SEO Info Box */}
        <div style={{ 
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)', 
          padding: '24px 32px', 
          borderRadius: '16px', 
          marginBottom: '40px',
          border: '1px solid #99f6e4'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f766e' }}>
            Why Use Our Free Templates?
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <span style={{ fontSize: '14px', color: '#374151' }}>Embassy-approved formats</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <span style={{ fontSize: '14px', color: '#374151' }}>Professionally written</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <span style={{ fontSize: '14px', color: '#374151' }}>Easy to customise</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <span style={{ fontSize: '14px', color: '#374151' }}>Instant download</span>
            </div>
          </div>
        </div>

        {/* Category Sections */}
        {categories.map(category => (
          <div key={category} style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: 'var(--text-primary)' }}>
              {category}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {resources.filter(r => r.category === category).map(resource => (
                <div key={resource.id} className="form-card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ 
                      width: '56px', 
                      height: '56px', 
                      background: 'var(--bg-secondary)', 
                      borderRadius: '12px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: '28px',
                      flexShrink: 0
                    }}>
                      {resource.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px', lineHeight: '1.3' }}>
                        {resource.title}
                      </h3>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', marginBottom: '12px' }}>
                        {resource.description}
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <span style={{ 
                            padding: '4px 10px', 
                            background: 'var(--bg-secondary)', 
                            borderRadius: '6px', 
                            fontSize: '11px', 
                            fontWeight: '600',
                            color: 'var(--text-muted)'
                          }}>
                            {resource.format}
                          </span>
                          <span style={{ 
                            padding: '4px 10px', 
                            background: 'var(--bg-secondary)', 
                            borderRadius: '6px', 
                            fontSize: '11px', 
                            fontWeight: '600',
                            color: 'var(--text-muted)'
                          }}>
                            {resource.size}
                          </span>
                        </div>
                        <button 
                          onClick={() => downloadResource(resource.id)}
                          style={{
                            padding: '8px 16px',
                            background: 'var(--accent-teal)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          ⬇️ Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* CTA Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
          padding: '40px', 
          borderRadius: '20px', 
          textAlign: 'center',
          color: 'white',
          marginBottom: '48px'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '12px' }}>
            Need a Verifiable Flight Reservation?
          </h2>
          <p style={{ opacity: 0.9, marginBottom: '20px', maxWidth: '500px', margin: '0 auto 20px' }}>
            Get a real PNR code that can be verified on airline websites. Perfect for visa applications.
          </p>
          <a href="/book" className="btn-primary" style={{ 
            background: 'white', 
            color: 'var(--accent-teal)',
            textDecoration: 'none',
            display: 'inline-block'
          }}>
            Book Your Dummy Ticket →
          </a>
        </div>
      </div>
    </div>
  );
};

// Referral Program Page
const ReferralPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  useSEO(
    'Referral Program | Earn Rewards | MyDummyTicket',
    'Join our referral program and earn 5% commission on every successful referral. Share your unique code with friends and family.'
  );
  
  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/referral');
      return;
    }
    
    const fetchReferral = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/referral/my-code`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setReferralData(response.data.referral);
        }
      } catch (err) {
        console.error('Error fetching referral:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReferral();
  }, [user, navigate, API]);
  
  const copyCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const shareUrl = `https://mydummyticket.com/order?ref=${referralData?.referral_code || ''}`;
  
  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#0f766e', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
          <p style={{ color: '#5f717e' }}>Loading your referral program...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', paddingTop: '100px', paddingBottom: '60px' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '12px' }}>
            🎁 Referral Program
          </h1>
          <p style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>
            Share your code and earn rewards when friends book!
          </p>
        </div>
        
        {/* Referral Code Card */}
        <div style={{ 
          background: 'white', 
          borderRadius: '20px', 
          padding: '32px', 
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          marginBottom: '32px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '14px', color: '#5f717e', marginBottom: '12px', fontWeight: '600' }}>
            YOUR UNIQUE REFERRAL CODE
          </div>
          <div style={{ 
            fontSize: '36px', 
            fontWeight: '800', 
            color: '#0f766e', 
            letterSpacing: '4px',
            marginBottom: '20px',
            fontFamily: 'monospace'
          }}>
            {referralData?.referral_code || 'N/A'}
          </div>
          <button
            onClick={copyCode}
            style={{
              padding: '14px 32px',
              background: copied ? '#22c55e' : 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {copied ? '✓ Copied!' : '📋 Copy Code'}
          </button>
        </div>
        
        {/* Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f766e' }}>{referralData?.referrals_count || 0}</div>
            <div style={{ fontSize: '13px', color: '#5f717e', fontWeight: '500' }}>Total Referrals</div>
          </div>
          <div style={{ background: 'white', padding: '24px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f766e' }}>${referralData?.total_commission || '0.00'}</div>
            <div style={{ fontSize: '13px', color: '#5f717e', fontWeight: '500' }}>Earnings</div>
          </div>
          <div style={{ background: 'white', padding: '24px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f766e' }}>5%</div>
            <div style={{ fontSize: '13px', color: '#5f717e', fontWeight: '500' }}>Commission Rate</div>
          </div>
        </div>
        
        {/* How It Works */}
        <div style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '24px', color: 'var(--text-primary)' }}>How It Works</h2>
          <div style={{ display: 'grid', gap: '20px' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ width: '40px', height: '40px', background: '#f0fdfa', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', color: '#0f766e', flexShrink: 0 }}>1</div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Share Your Code</div>
                <div style={{ fontSize: '14px', color: '#5f717e' }}>Share your unique referral code with friends, family, or on social media.</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ width: '40px', height: '40px', background: '#f0fdfa', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', color: '#0f766e', flexShrink: 0 }}>2</div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Friends Get 5% Off</div>
                <div style={{ fontSize: '14px', color: '#5f717e' }}>When they enter your code at checkout, they receive a 5% discount on their order.</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ width: '40px', height: '40px', background: '#f0fdfa', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', color: '#0f766e', flexShrink: 0 }}>3</div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>You Earn 5% Commission</div>
                <div style={{ fontSize: '14px', color: '#5f717e' }}>For every successful referral, you earn 5% of the order value as commission.</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Share Links */}
        <div style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '24px', color: 'var(--text-primary)' }}>Share Your Link</h2>
          <div style={{ 
            background: '#f8fafc', 
            padding: '16px', 
            borderRadius: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            marginBottom: '20px'
          }}>
            <input 
              type="text" 
              readOnly 
              value={shareUrl}
              style={{ 
                flex: 1, 
                background: 'transparent', 
                border: 'none', 
                fontSize: '14px', 
                color: '#374151',
                outline: 'none'
              }}
            />
            <button
              onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              style={{
                padding: '10px 20px',
                background: '#0f766e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {copied ? '✓ Copied' : 'Copy Link'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a 
              href={`mailto:?subject=Get%205%25%20off%20on%20MyDummyTicket&body=Use%20my%20referral%20code%20${referralData?.referral_code}%20to%20get%205%25%20off!%20${encodeURIComponent(shareUrl)}`}
              style={{ padding: '12px 20px', background: '#f1f5f9', borderRadius: '10px', textDecoration: 'none', color: '#374151', fontWeight: '600', fontSize: '14px' }}
            >
              📧 Email
            </a>
            <a 
              href={`https://twitter.com/intent/tweet?text=Get%205%25%20off%20on%20dummy%20tickets%20with%20my%20code%20${referralData?.referral_code}!&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ padding: '12px 20px', background: '#f1f5f9', borderRadius: '10px', textDecoration: 'none', color: '#374151', fontWeight: '600', fontSize: '14px' }}
            >
              🐦 Twitter
            </a>
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ padding: '12px 20px', background: '#f1f5f9', borderRadius: '10px', textDecoration: 'none', color: '#374151', fontWeight: '600', fontSize: '14px' }}
            >
              📘 Facebook
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
